import {
  AUTO_TRADE,
  BALANCE,
  BUILDINGS,
  BUILDING_IDS,
  CLOUD_LEVELS,
  COMBAT,
  CRISIS_DIRECTOR,
  MEGASTRUCTURE,
  PRESTIGE,
  VICTORY,
  CRISIS_EFFECTS,
  CRISIS_KINDS,
  ECONOMY,
  PROJECTS,
  PROJECT_RULES,
  SHIELD_CRISIS_FACTOR,
  INSTABILITY,
  AGREEMENT_IDS,
  CURRENCY,
  DIPLOMACY,
  INVASION,
  RAID,
  SHIP_COMBAT,
  FORMATION,
  STANCE_IDS,
  TRADE,
  TRADE_AGREEMENT,
  FACTIONS,
  FACTION_ECONOMY,
  FINDS,
  FIND_CHOICE,
  INFLUENCE,
  LEVERS,
  LEVER_EFFECTS,
  LEVER_IDS,
  MARKET_EVENTS,
  OUTPOSTS,
  PIRATE_BANDS,
  TRADE_GOODS,
  PIRATE_RULES,
  MARKET,
  RESEARCH,
  TECHS,
  FLEET,
  RESERVE,
  SHIP_CLASSES,
  TRADE_GOOD_IDS,
  STARTING_CASH,
  STARTING_SHIPS,
  STAR_CLASSES,
  solarFlux,
  startingGoods,
  type AgreementId,
  type BuildingId,
  type CloudLevelId,
  type CrisisKindId,
  type CountdownVictoryId,
  type MatchModeId,
  type ProjectId,
  type FactionId,
  type FindId,
  type LeverId,
  type OutpostKind,
  type ShipClassId,
  type StanceId,
  type TechId,
  type SlotLayer,
  type TradeGoodId,
  type RichnessId,
} from '@sd/content';
import {
  computeRate,
  createCurrency,
  emit,
  recordCycle,
  startCrisis,
  toLocal,
  tradeShares,
  windowOutput,
  type CurrencyState,
} from './currency';
import {
  applyQuote,
  createMarket,
  isTradeGood,
  quote,
  resetCycleVolume,
  revertPrices,
  roundMoney,
  shockPrice,
  windowNet,
  type MarketState,
  type Quote,
  type TradeSide,
} from './market';
import {
  buildBlocker,
  buildCost,
  canAfford,
  createPlanet,
  payCost,
  populationNeeds,
  runPlanetCycle,
  startConstruction,
  upgradeBlocker,
  type PlanetState,
} from './planet';
import { rangedRound, type Combatant } from './combat';
import { cargoTotal, contractSlots, involves, type Caravan, type Contract } from './contracts';
import {
  fleetDamage,
  garrisonStrength,
  hasAgreement,
  pairIs,
  planetGuns,
  routeLevel,
  sameTeam,
  teamAlliances,
  teamOf,
  type Agreement,
  type PendingWar,
  type Proposal,
  type Raid,
  type Siege,
  type TradeRoute,
} from './diplomacy';
import {
  asteroidHex,
  cloudAt,
  createExploration,
  drawFind,
  liveCloudAt,
  type ExplorationState,
} from './exploration';
import {
  fleetLimit,
  fleetRetreatAt,
  fleetStance,
  fleetTicksPerHex,
  fleetUpkeep,
  freeShipNumber,
  hullShare,
  isArmed,
  leadShip,
  shipHull,
  spaceportLevel,
  findPath,
  isShipClass,
  moveFleet,
  parallelBuilds,
  shipsOf,
  type BuildOrder,
  type FleetState,
  type ShipState,
} from './fleet';
import { hexDistance, hexKey, type Hex } from './hex';
import {
  contractBlock,
  hasEmbargo,
  influenceGain,
  isSanctioned,
  tariffOwners,
  type Lever,
  type MarketWarning,
} from './levers';
import {
  inZone,
  outpostLimit,
  outpostSites,
  outpostUpkeep,
  siteAt,
  type OutpostState,
} from './outposts';
import { createReserve, updateReserve, type ReserveState } from './reserve';
import {
  addInstability,
  chooseCrisis,
  createInstability,
  instabilityGain,
  thresholdsReached,
  type Crisis,
  type InstabilityState,
} from './instability';
import {
  coveredPlayers,
  emptyContribution,
  goodsValue,
  projectHex,
  projectOutcome,
  projectSize,
  projectTotal,
  scienceValue,
  sectorOf,
  shipValue,
  type Contribution,
  type Project,
  type ProjectOutcome,
} from './projects';
import { Rng, type RngState } from './rng';
import { standings, type Countdown, type MatchResult, type Megastructure } from './victory';
import {
  createResearch,
  fragmentTargets,
  isTech,
  researchBlocker,
  researchSlots,
  shareScience,
  techMods,
  techPrice,
  type ResearchState,
} from './techs';
import { explore, type Explored } from './explored';
import { generateSystem, type DustCloud, type PlayerSetup, type StarSystem } from './system';

/**
 * The match state machine. The ONLY way to change a match is
 * `step(state, commands)`: hosts (server, offline client, bots) collect
 * player commands and call it once per tick. Same seed + same commands
 * = same match, which gives us networking, replays and bots for free.
 */

/** A trading rule (#123): sell above `keep` while the good is made, or buy up to it. */
export interface AutoTradeRule {
  readonly mode: 'sell' | 'buy';
  readonly keep: number;
}

/** A find on offer (#105): a cache names its good. */
export interface FindCard {
  readonly find: Exclude<FindId, 'pirates'>;
  readonly good?: TradeGoodId;
}

/** A find waiting for its player to pick one card; the first is taken when time runs out. */
export interface FindOffer {
  readonly id: number;
  readonly playerId: string;
  readonly cloudId: string;
  readonly level: CloudLevelId;
  readonly cards: readonly FindCard[];
  readonly expiresAt: number;
}

export interface PlayerState {
  readonly id: string;
  readonly faction: FactionId;
  /** Teammates share a number and a permanent alliance, and win together (#137). */
  readonly team?: number;
  readonly pausesLeft: number;
  /** Money, in the player's own currency (GDD §6). */
  readonly cash: number;
  readonly currency: CurrencyState;
  readonly goods: Readonly<Record<TradeGoodId, number>>;
  /** The capital and its economy (GDD §5). */
  readonly planet: PlanetState;
  /** Ships being built in the spaceport, in order (GDD §5). */
  readonly shipyard: readonly BuildOrder[];
  /** False after a cycle whose fleet upkeep the player could not pay: fleets crawl. */
  readonly supplied: boolean;
  /** Technology fragments from derelict ships, spent on research discounts (#27). */
  readonly techFragments: number;
  /** Pirate bands this player helped destroy (Prestige, #30). */
  readonly pirateKills: number;
  /** 0..100; peacetime raids and broken pacts cost reputation (GDD §8). */
  readonly reputation: number;
  /** Overlord's id when this player is a vassal (GDD §8), else null. */
  readonly vassalOf: string | null;
  /** Technologies researched (GDD §10). */
  readonly techs: readonly TechId[];
  /** Research under way, science put in, fragment discounts (#27). */
  readonly research: ResearchState;
  /** Prestige (GDD §10): from joint projects now, more sources with #30. */
  readonly prestige: number;
  /** A crisis blackout: the capital runs at `energy` until the tick. */
  readonly blackout: { readonly until: number; readonly energy: number } | null;
  /** Tick this player last failed a crisis as an overlord (#29); lets vassals rebel. */
  readonly failedCrisisAt: number | null;
  /** 0..100, earned each cycle from trade and spent on levers (GDD §6). */
  readonly influence: number;
  /** ГС of goods this player delivered by contract in the current cycle. */
  readonly cycleExports: number;
  /** «Торговые алгоритмы» (#123): a rule per good, run once a cycle. */
  readonly autoTrade?: Readonly<Partial<Record<TradeGoodId, AutoTradeRule>>>;
}

export interface PauseState {
  readonly by: string;
  readonly ticksLeft: number;
}

export type MatchStatus = 'running' | 'ended';

export interface GameState {
  /** Simulation time in ticks; does not advance while paused. */
  readonly tick: number;
  readonly status: MatchStatus;
  readonly rng: RngState;
  readonly system: StarSystem;
  readonly players: readonly PlayerState[];
  readonly pause: PauseState | null;
  readonly market: MarketState;
  /** Reserve currency status (GDD §6). */
  readonly reserve: ReserveState;
  readonly fleets: readonly FleetState[];
  readonly exploration: ExplorationState;
  readonly outposts: readonly OutpostState[];
  /** Pairs of players at war (GDD §8). Declarations and peace arrive with diplomacy (#24). */
  readonly wars: readonly War[];
  /** Hexes with a battle in progress, by hex key. */
  readonly battles: readonly string[];
  /** Wreckage left by battles (GDD §8): metals for salvage, fuel for the instability index (#28). */
  readonly debris: readonly Debris[];
  /** What each player has seen at least once (#97, explored.ts). */
  readonly explored: Explored;
  /** Pirate bands on the map and bands about to appear (GDD §7). */
  readonly pirates: readonly PirateBand[];
  readonly pirateWarnings: readonly PirateWarning[];
  /** Declared wars waiting to start (GDD §8: 30 s, visible to all). */
  readonly pendingWars: readonly PendingWar[];
  readonly agreements: readonly Agreement[];
  readonly proposals: readonly Proposal[];
  /** Deliveries between trade partners: the route levels of trade agreements (#74). */
  readonly tradeRoutes: readonly TradeRoute[];
  readonly raids: readonly Raid[];
  readonly sieges: readonly Siege[];
  /** Contracts between players, proposed and active (GDD §6). */
  readonly contracts: readonly Contract[];
  /** The instability index, visible to all (GDD §9). */
  readonly instability: InstabilityState;
  /** Crises planned or announced, not yet struck. */
  readonly crises: readonly Crisis[];
  /** Joint projects open against those crises (GDD §9). */
  readonly projects: readonly Project[];
  /** How the final crisis ended, once its last phase has struck: the system saved, or not. */
  readonly finalCrisis: 'averted' | 'failed' | null;
  /** How each stage of the stabilizer went, phase by phase. */
  readonly finalStages: readonly ProjectOutcome[];
  /** Lobby choice: end at the final crisis, or play on after saving the system (GDD §9). */
  readonly mode: MatchModeId;
  /** Lobby choice (#105, a test): a find is picked from cards instead of given. */
  readonly findChoice: boolean;
  /** Finds waiting for their player to pick a card. */
  readonly findOffers: readonly FindOffer[];
  /** Dyson swarms in the corona (GDD §10, technological victory). */
  readonly megastructures: readonly Megastructure[];
  /** Victory countdowns running, visible to all. */
  readonly countdowns: readonly Countdown[];
  /** «Жизнь после хаоса»: the match ends at this tick after the final crisis is beaten. */
  readonly endsAt: number | null;
  /** How the match ended; null while it runs. */
  readonly result: MatchResult | null;
  /** Embargoes, tariffs, dumping and sanctions in force or gathering backers (GDD §6). */
  readonly levers: readonly Lever[];
  /** A glut or shortage announced for the next cycle (at most one). */
  readonly marketWarnings: readonly MarketWarning[];
  /** No new exchange event is announced before this tick. */
  readonly marketQuietUntil: number;
  /** Next id for ships and fleets: ids stay unique and deterministic. */
  readonly nextId: number;
}

export type Command =
  | { readonly type: 'pause'; readonly playerId: string }
  | { readonly type: 'resume'; readonly playerId: string }
  | {
      readonly type: 'marketBuy' | 'marketSell';
      readonly playerId: string;
      /** Validated at runtime: commands arrive from the network. */
      readonly good: TradeGoodId;
      readonly quantity: number;
    }
  | {
      readonly type: 'build';
      readonly playerId: string;
      readonly building: BuildingId;
      readonly layer: SlotLayer;
    }
  | {
      readonly type: 'upgrade';
      readonly playerId: string;
      /** Index into the player's `planet.buildings`. */
      readonly index: number;
    }
  | {
      /** Print money: cash now, a weaker currency from now on (GDD §6). */
      readonly type: 'emit';
      readonly playerId: string;
      readonly amount: number;
    }
  | { readonly type: 'buildShip'; readonly playerId: string; readonly ship: ShipClassId }
  /** Sets or clears a good's trading rule (#123); needs «Торговые алгоритмы». */
  | {
      readonly type: 'setAutoTrade';
      readonly playerId: string;
      readonly good: TradeGoodId;
      readonly rule: AutoTradeRule | null;
    }
  /** Takes one card of a find offer (#105). */
  | {
      readonly type: 'pickFind';
      readonly playerId: string;
      readonly offerId: number;
      readonly card: number;
    }
  | {
      /** Send a fleet to a hex; its own hex = stop. */
      readonly type: 'moveFleet';
      readonly playerId: string;
      readonly fleetId: number;
      readonly to: Hex;
    }
  | {
      /** Split the listed ships off into a new fleet in the same hex. */
      readonly type: 'splitFleet';
      readonly playerId: string;
      readonly fleetId: number;
      readonly shipIds: readonly number[];
    }
  | {
      /** Merge fleets in one hex into the first of them. */
      readonly type: 'mergeFleets';
      readonly playerId: string;
      readonly fleetIds: readonly number[];
    }
  | {
      /** Scan the dust cloud the fleet sits in, find after find (GDD §7). */
      readonly type: 'scan';
      readonly playerId: string;
      readonly fleetId: number;
    }
  | {
      /** A transport of the fleet settles the site it sits on and becomes the station (GDD §5). */
      readonly type: 'buildOutpost';
      readonly playerId: string;
      readonly fleetId: number;
    }
  | {
      /** Combat stance and the hull share at which the fleet retreats home (GDD §8). */
      readonly type: 'setStance';
      readonly playerId: string;
      readonly fleetId: number;
      readonly stance: StanceId;
      readonly retreatAt?: number;
    }
  | { readonly type: 'declareWar'; readonly playerId: string; readonly target: string }
  | {
      /** Propose peace or an agreement; the other side accepts with the same kind. */
      readonly type: 'propose' | 'accept';
      readonly playerId: string;
      readonly other: string;
      readonly kind: AgreementId | 'peace';
    }
  | {
      readonly type: 'cancelAgreement';
      readonly playerId: string;
      readonly other: string;
      readonly kind: AgreementId;
    }
  | {
      /** Plunder the enemy capital or outpost the fleet sits on (GDD §8). */
      readonly type: 'raid';
      readonly playerId: string;
      readonly fleetId: number;
    }
  | {
      /** Land the fleet's transports on the enemy capital it holds (GDD §8). */
      readonly type: 'invade';
      readonly playerId: string;
      readonly fleetId: number;
    }
  | { readonly type: 'declareIndependence'; readonly playerId: string }
  | {
      /**
       * Offer a contract to `other`: `role` says whether the proposer sells or
       * buys. `price` in ГС per unit, or `barter` instead (GDD §6).
       */
      readonly type: 'proposeContract';
      readonly playerId: string;
      readonly other: string;
      readonly role: 'sell' | 'buy';
      readonly good: TradeGoodId;
      readonly quantity: number;
      readonly price?: number;
      readonly barter?: { readonly good: TradeGoodId; readonly quantity: number };
      readonly deliveries: number;
    }
  | {
      readonly type: 'acceptContract' | 'cancelContract';
      readonly playerId: string;
      readonly contractId: number;
    }
  | {
      /** The seller sends a fleet with cargo holds to carry the contract. */
      readonly type: 'assignCaravan';
      readonly playerId: string;
      readonly contractId: number;
      readonly fleetId: number;
    }
  | {
      /**
       * Spend influence on a lever (GDD §6): `target` for embargo and
       * sanctions, `good` and `quantity` for dumping.
       */
      readonly type: 'useLever';
      readonly playerId: string;
      readonly lever: LeverId;
      readonly target?: string;
      readonly good?: TradeGoodId;
      readonly quantity?: number;
    }
  | { readonly type: 'joinSanctions'; readonly playerId: string; readonly leverId: number }
  | {
      /**
       * Give to a joint project (GDD §9): goods it takes, science, or a whole
       * fleet that has flown to the project's hex.
       */
      readonly type: 'contributeProject';
      readonly playerId: string;
      readonly projectId: number;
      readonly what: TradeGoodId | 'science' | 'fleet';
      readonly quantity?: number;
      readonly fleetId?: number;
    }
  | {
      /** A transport in a free corona hex starts the Dyson swarm (needs «Управление звездой»). */
      readonly type: 'buildMegastructure';
      readonly playerId: string;
      readonly fleetId: number;
    }
  | {
      /** Start (or resume) researching a technology; cancelling keeps what was put in. */
      readonly type: 'startResearch' | 'cancelResearch';
      readonly playerId: string;
      readonly tech: TechId;
    }
  | {
      /** A gift: half is lost on the way and everyone sees it (GDD §12). */
      readonly type: 'transfer';
      readonly playerId: string;
      readonly to: string;
      readonly what: TradeGoodId | 'cash';
      readonly amount: number;
    };

export type GameEvent =
  | { readonly type: 'paused'; readonly playerId: string; readonly pausesLeft: number }
  | { readonly type: 'resumed'; readonly playerId: string | null }
  | {
      readonly type: 'traded';
      readonly playerId: string;
      /** The deal on the exchange, in ГС. */
      readonly quote: Quote;
      /** What the player paid (buy) or received (sell), in their currency. */
      readonly local: number;
      /** Conversion fee, in the player's currency. */
      readonly conversion: number;
      /** Seigniorage paid to the reserve issuer out of the exchange fee, ГС. */
      readonly seigniorage: number;
      /** Sanctions surcharge, in the player's currency (GDD §6). */
      readonly surcharge: number;
    }
  | { readonly type: 'reserveDeclared'; readonly playerId: string }
  | {
      readonly type: 'shipQueued' | 'shipBuilt';
      readonly playerId: string;
      readonly ship: ShipClassId;
      /** The new fleet, once built. */
      readonly fleetId?: number;
    }
  | { readonly type: 'fleetArrived'; readonly playerId: string; readonly fleetId: number }
  | { readonly type: 'upkeepUnpaid'; readonly playerId: string }
  | {
      readonly type: 'found';
      readonly playerId: string;
      readonly cloudId: string;
      readonly find: FindId;
      /** What the find gave, for the UI: goods, science, fragments, an asteroid site. */
      readonly gains: Readonly<Record<string, number | string>>;
    }
  | { readonly type: 'cloudExhausted'; readonly cloudId: string }
  | {
      readonly type: 'findOffered';
      readonly playerId: string;
      readonly offerId: number;
      readonly cloudId: string;
    }
  | { readonly type: 'battleStarted' | 'battleEnded'; readonly hex: Hex }
  /** A round of fire (#117), for the map's bolts: one shot per pair of fleets. */
  | {
      readonly type: 'volley';
      readonly shots: readonly {
        readonly from: Hex;
        readonly to: Hex;
        readonly owner: string;
        readonly cls: ShipClassId;
      }[];
    }
  | {
      readonly type: 'warDeclared';
      readonly by: string;
      readonly target: string;
      readonly startsAt: number;
    }
  | { readonly type: 'warStarted' | 'peaceMade'; readonly a: string; readonly b: string }
  | {
      readonly type: 'proposed' | 'agreed' | 'agreementEnded';
      readonly kind: AgreementId | 'peace';
      readonly a: string;
      readonly b: string;
    }
  | {
      readonly type: 'raidStarted' | 'raidFailed';
      readonly playerId: string;
      readonly victim: string;
      readonly fleetId: number;
    }
  | {
      readonly type: 'raided';
      readonly playerId: string;
      readonly victim: string;
      readonly loot: Readonly<Partial<Record<TradeGoodId, number>>>;
    }
  | {
      /** A Reforged raid in peacetime: instant war, reputation lost (GDD §8). */
      readonly type: 'peacetimeRaid';
      readonly playerId: string;
      readonly victim: string;
    }
  | {
      readonly type: 'siegeStarted' | 'siegeBroken';
      readonly playerId: string;
      readonly victim: string;
    }
  | { readonly type: 'vassalized'; readonly vassal: string; readonly overlord: string }
  | {
      readonly type: 'contractProposed' | 'contractSigned' | 'contractCompleted';
      readonly contractId: number;
      readonly seller: string;
      readonly buyer: string;
    }
  | {
      readonly type: 'contractBroken';
      readonly contractId: number;
      /** Who broke it, and lost reputation for it. */
      readonly by: string;
    }
  | {
      readonly type: 'delivered';
      readonly contractId: number;
      /** Value of the delivery in ГС; the exporter earns trade income twice, the importer once. */
      readonly gs: number;
    }
  | {
      readonly type: 'caravanRobbed';
      readonly contractId: number;
      /** A player id, or `pirates`. */
      readonly by: string;
      readonly loot: Readonly<Partial<Record<TradeGoodId, number>>>;
    }
  | {
      readonly type: 'transferred';
      readonly from: string;
      readonly to: string;
      readonly what: TradeGoodId | 'cash';
      readonly sent: number;
      readonly arrived: number;
    }
  | { readonly type: 'independence'; readonly vassal: string; readonly overlord: string }
  | {
      readonly type: 'leverUsed';
      readonly playerId: string;
      readonly leverId: number;
      readonly lever: LeverId;
      readonly target: string | null;
      readonly good: TradeGoodId | null;
    }
  | { readonly type: 'sanctionsJoined'; readonly playerId: string; readonly leverId: number }
  | {
      readonly type: 'researchStarted' | 'researchCancelled' | 'researchCompleted';
      readonly playerId: string;
      readonly tech: TechId;
    }
  | {
      readonly type: 'sanctionsImposed';
      readonly leverId: number;
      readonly target: string;
      readonly by: readonly string[];
    }
  | {
      /** A lever ran out; `lapsed` when sanctions never found a second backer (refunded). */
      readonly type: 'leverEnded';
      readonly leverId: number;
      readonly lever: LeverId;
      readonly lapsed: boolean;
    }
  | {
      /** A caravan entered a zone with a tariff (GDD §6). */
      readonly type: 'tariffPaid';
      readonly contractId: number;
      readonly payer: string;
      readonly to: string;
      readonly gs: number;
    }
  | {
      /** A glut or shortage: announced a cycle ahead, then it hits the price — or not. */
      readonly type: 'marketEvent';
      readonly good: TradeGoodId;
      readonly kind: 'glut' | 'shortage';
      readonly phase: 'warning' | 'struck' | 'averted';
    }
  | {
      readonly type: 'tributePaid';
      readonly vassal: string;
      readonly overlord: string;
      readonly gs: number;
    }
  | {
      /** Pirates will appear here in `inTicks` (GDD §12: time for the explorer to leave). */
      readonly type: 'piratesWarning';
      readonly playerId: string;
      readonly cloudId: string;
      readonly hex: Hex;
      readonly inTicks: number;
    }
  | {
      readonly type: 'piratesSpawned' | 'piratesRoaming';
      readonly fleetId: number;
      readonly cloudId: string;
    }
  | {
      readonly type: 'piratesDefeated';
      readonly fleetId: number;
      /** Players whose fleets were in the hex: they share the loot. */
      readonly winners: readonly string[];
      readonly loot: { readonly alloys: number; readonly exotics: number };
    }
  | {
      readonly type: 'shipDestroyed';
      readonly playerId: string;
      readonly ship: ShipClassId;
      readonly hex: Hex;
    }
  | {
      readonly type: 'fleetDestroyed' | 'fleetRetreating';
      readonly playerId: string;
      readonly fleetId: number;
      /** The ship it went by (#154), for the note that names it. */
      readonly lead?: ShipState;
    }
  | {
      readonly type: 'outpostFounded' | 'outpostReady';
      readonly playerId: string;
      readonly outpostId: string;
      readonly kind: OutpostKind;
    }
  | { readonly type: 'reserveLost'; readonly playerId: string; readonly rate: number }
  | {
      readonly type: 'emitted';
      readonly playerId: string;
      readonly amount: number;
      readonly rate: number;
    }
  | {
      readonly type: 'constructionStarted' | 'constructionCompleted';
      readonly playerId: string;
      readonly index: number;
      readonly building: BuildingId;
      readonly level: number;
    }
  | { readonly type: 'starvation'; readonly playerId: string; readonly population: number }
  | { readonly type: 'commandRejected'; readonly command: Command; readonly reason: string }
  /** A trade route between partners reached a new level (#74). */
  | {
      readonly type: 'routeLevelUp';
      readonly a: string;
      readonly b: string;
      readonly level: number;
    }
  | {
      /** The index crossed a threshold: a crisis is on its way, unless it is too late for one. */
      readonly type: 'instabilityThreshold';
      readonly threshold: number;
      readonly value: number;
    }
  | {
      /** What, where, when, and which project helps (GDD §9). `early`: heard thanks to foresight. */
      readonly type: 'crisisWarning';
      readonly playerId: string;
      readonly crisisId: number;
      readonly kind: CrisisKindId;
      readonly sector: number | null;
      readonly strikeAt: number;
      readonly project: ProjectId;
      readonly early: boolean;
      /** Phase of the final crisis, or null. */
      readonly phase: number | null;
    }
  | {
      readonly type: 'projectOpened';
      readonly projectId: number;
      readonly crisisId: number;
      readonly kind: ProjectId;
      readonly hex: Hex;
      readonly threshold: number;
      readonly full: number;
      readonly deadline: number;
    }
  | {
      readonly type: 'projectContribution';
      readonly projectId: number;
      readonly playerId: string;
      /** Value given, ГС, and the project's total after it. */
      readonly points: number;
      readonly total: number;
    }
  | {
      /** At the strike: built in full, built to the threshold (covers contributors), or refunded. */
      readonly type: 'projectResolved';
      readonly projectId: number;
      readonly crisisId: number;
      readonly outcome: ProjectOutcome;
      readonly prestige: Readonly<Record<string, number>>;
    }
  | {
      /** What a crisis did to a player it hit. */
      readonly type: 'crisisDamage';
      readonly playerId: string;
      readonly kind: CrisisKindId;
      readonly populationLost: number;
      readonly blackoutCycles: number;
      readonly outpostsDown: number;
      readonly shipsLost: number;
    }
  | {
      readonly type: 'megastructureStarted' | 'megastructureStage';
      readonly playerId: string;
      /** Stages finished so far. */
      readonly stages: number;
    }
  | {
      /** A hostile fleet held the site: a stage is lost (GDD §10). */
      readonly type: 'megastructureSetBack';
      readonly playerId: string;
      readonly by: string;
      readonly stages: number;
    }
  | {
      readonly type:
        'victoryCountdown' | 'countdownStopped' | 'countdownPaused' | 'countdownResumed';
      readonly playerId: string;
      readonly kind: CountdownVictoryId;
      readonly endsAt: number;
    }
  | { readonly type: 'matchEnded'; readonly result: MatchResult }
  | {
      readonly type: 'crisisStruck';
      readonly crisisId: number;
      readonly kind: CrisisKindId;
      readonly sector: number | null;
    }
  | { readonly type: 'sessionLimitReached' };

export interface War {
  readonly a: string;
  readonly b: string;
  /** Tick the fighting started; absent in matches saved before #74. */
  readonly since?: number;
}

export interface PirateBand {
  readonly fleetId: number;
  readonly level: CloudLevelId;
  readonly cloudId: string;
  readonly spawnedTick: number;
  /** Level-3 bands left alone start roaming the system. */
  readonly roaming: boolean;
}

export interface PirateWarning {
  readonly cloudId: string;
  readonly hex: Hex;
  readonly level: CloudLevelId;
  readonly spawnTick: number;
}

export interface Debris {
  readonly hex: Hex;
  readonly metals: number;
}

/** Owner id of pirate fleets (#23): hostile to everyone. */
export const PIRATES = 'pirates';

/**
 * Hexes an unarmed fleet of `owner` may neither cross nor stop in (#158, the author's
 * decision of 28.10.2026): wherever ships of someone at war with it stand.
 */
export function enemyHexes(state: Pick<GameState, 'wars' | 'fleets'>, owner: string): Set<string> {
  const out = new Set<string>();
  for (const f of state.fleets) if (atWar(state, owner, f.owner)) out.add(hexKey(f.hex));
  return out;
}

/**
 * Whether an aggressive formation on the move holds its hex (#160): an enemy is within
 * reach of its longest gun.
 */
export function holdsAtRange(state: GameState, f: FleetState): boolean {
  if (!isFormation(f) || f.retreating || fleetStance(f) !== 'aggressive') return false;
  const range = Math.max(
    0,
    ...f.ships.filter((s) => SHIP_COMBAT[s.cls].damage > 0).map((s) => SHIP_COMBAT[s.cls].range),
  );
  if (range === 0) return false;
  return state.fleets.some(
    (b) => b.id !== f.id && atWar(state, f.owner, b.owner) && hexDistance(f.hex, b.hex) <= range,
  );
}

/** A player's fleet of several ships: a formation (#160), with its drill, cover and convoy. */
export const isFormation = (f: FleetState): boolean => f.owner !== PIRATES && f.ships.length > 1;

/** A formation's drill (#160): +10% damage for each armed ship beyond the first, to +20%. */
export function formationDrill(f: FleetState): number {
  if (!isFormation(f)) return 1;
  const armed = f.ships.filter((s) => SHIP_COMBAT[s.cls].damage > 0).length;
  return 1 + Math.min(FORMATION.drillMax, FORMATION.drillPerShip * Math.max(0, armed - 1));
}

export function atWar(state: Pick<GameState, 'wars'>, a: string, b: string): boolean {
  if (a === b) return false;
  if (a === PIRATES || b === PIRATES) return true;
  return state.wars.some((w) => (w.a === a && w.b === b) || (w.a === b && w.b === a));
}

export interface StepResult {
  readonly state: GameState;
  readonly events: readonly GameEvent[];
}

export interface CreateGameOptions {
  readonly seed: string;
  readonly players: readonly PlayerSetup[];
  /** Default: the match ends with the final crisis (GDD §9). */
  readonly mode?: MatchModeId;
  /** How rich the system is (#98); default «balanced». */
  readonly richness?: RichnessId;
  /** A find is picked from cards (#105, a test); default off. */
  readonly findChoice?: boolean;
}

export function createGame({
  seed,
  players,
  mode = 'crisisFinale',
  richness = 'balanced',
  findChoice = false,
}: CreateGameOptions): GameState {
  const system = generateSystem({ seed, players, richness });
  const luminosity = STAR_CLASSES[system.starClass].luminositySun;
  const home = (playerId: string) => {
    const planet = system.planets.find((pl) => pl.homeOf === playerId);
    const orbit = planet && system.orbits[planet.orbit];
    if (!planet || !orbit) throw new Error(`no capital generated for ${playerId}`);
    return { planet, flux: solarFlux(orbit.au, luminosity) };
  };
  // Every capital sees its surroundings from the first tick (#97).
  return explore(
    {
      tick: 0,
      status: 'running',
      rng: Rng.fromSeed(`match:${seed}`).getState(),
      system,
      players: players.map((p) => ({
        id: p.id,
        faction: p.faction,
        ...(p.team !== undefined ? { team: p.team } : {}),
        pausesLeft: BALANCE.pausesPerPlayer,
        cash: STARTING_CASH,
        currency: createCurrency(),
        goods: startingGoods(p.faction),
        planet: (() => {
          const { planet, flux } = home(p.id);
          return createPlanet(p.faction, planet.id, planet.kind, flux);
        })(),
        shipyard: [],
        supplied: true,
        techFragments: 0,
        pirateKills: 0,
        reputation: DIPLOMACY.reputationStart,
        vassalOf: null,
        techs: [],
        research: createResearch(),
        prestige: 0,
        blackout: null,
        failedCrisisAt: null,
        influence: 0,
        cycleExports: 0,
      })),
      pause: null,
      market: createMarket(),
      reserve: createReserve(),
      exploration: createExploration(system),
      outposts: [],
      wars: [],
      battles: [],
      debris: [],
      explored: {},
      pirates: [],
      pirateWarnings: [],
      pendingWars: [],
      agreements: teamAlliances(players),
      tradeRoutes: [],
      proposals: [],
      raids: [],
      sieges: [],
      contracts: [],
      instability: createInstability(),
      crises: [],
      projects: [],
      finalCrisis: null,
      finalStages: [],
      mode,
      findChoice,
      findOffers: [],
      megastructures: [],
      countdowns: [],
      endsAt: null,
      result: null,
      levers: [],
      marketWarnings: [],
      marketQuietUntil: 0,
      ...startingFleets(system, players),
    },
    true,
  );
}

/** Every capital's starting ships, as one fleet per player. */
function startingFleets(
  system: StarSystem,
  players: readonly PlayerSetup[],
): { fleets: FleetState[]; nextId: number } {
  let nextId = 1;
  const fleets = players.map((p): FleetState => {
    const ships = STARTING_SHIPS.map((cls, i) => {
      const n = STARTING_SHIPS.slice(0, i).filter((c) => c === cls).length + 1;
      return { id: nextId++, cls, ...(n > 1 ? { n } : {}) };
    });
    return {
      id: nextId++,
      owner: p.id,
      hex: system.planets.find((pl) => pl.homeOf === p.id)!.hex,
      ships,
      path: [],
      progress: 0,
      scan: null,
    };
  });
  return { fleets: STARTING_SHIPS.length ? fleets : [], nextId };
}

/** The hex of a player's capital. */
export function capitalHex(state: GameState, playerId: string): Hex {
  const planet = state.system.planets.find((p) => p.homeOf === playerId);
  if (!planet) throw new Error(`no capital for ${playerId}`);
  return planet.hex;
}

function applyCommand(state: GameState, command: Command, events: GameEvent[]): GameState {
  const reject = (reason: string) => {
    events.push({ type: 'commandRejected', command, reason });
    return state;
  };
  const player = state.players.find((p) => p.id === command.playerId);
  if (!player) return reject('unknown player');

  switch (command.type) {
    case 'pause': {
      if (state.pause) return reject('already paused');
      if (player.pausesLeft <= 0) return reject('no pauses left');
      const pausesLeft = player.pausesLeft - 1;
      events.push({ type: 'paused', playerId: player.id, pausesLeft });
      return {
        ...state,
        pause: { by: player.id, ticksLeft: BALANCE.pauseTicks },
        players: state.players.map((p) => (p.id === player.id ? { ...p, pausesLeft } : p)),
      };
    }
    case 'resume': {
      if (!state.pause) return reject('not paused');
      if (state.pause.by !== player.id)
        return reject('only the player who paused can resume early');
      events.push({ type: 'resumed', playerId: player.id });
      return { ...state, pause: null };
    }
    case 'marketBuy':
    case 'marketSell': {
      // Trades are instant, so they wait for the game to run (GDD §2: pauses freeze time).
      if (state.pause) return reject('paused');
      if (!isTradeGood(command.good)) return reject('unknown good');
      if (
        !Number.isInteger(command.quantity) ||
        command.quantity < 1 ||
        command.quantity > MARKET.maxOrderUnits
      ) {
        return reject(`quantity must be 1–${MARKET.maxOrderUnits}`);
      }
      const side = command.type === 'marketBuy' ? 'buy' : 'sell';
      const deal = tradeTerms(state, player.id, command.good, side, command.quantity);
      if (!deal.ok) return reject(deal.reason);
      const { quote: q, local, conversion, surcharge } = deal;
      const issuer = state.reserve.issuer;
      const sign = side === 'buy' ? 1 : -1;
      // Seigniorage: the issuer's cut of every foreign deal, paid out of the exchange fee.
      const seigniorage =
        issuer !== null && issuer !== player.id ? q.gross * seigniorageOf(state, issuer) : 0;
      events.push({
        type: 'traded',
        playerId: player.id,
        quote: q,
        local,
        conversion,
        seigniorage,
        surcharge,
      });
      return {
        ...state,
        market: applyQuote(state.market, q),
        players: state.players.map((p) =>
          p.id === issuer && seigniorage > 0
            ? { ...p, cash: roundMoney(p.cash + seigniorage / p.currency.rate) }
            : p.id === player.id
              ? {
                  ...p,
                  cash: roundMoney(p.cash - sign * local),
                  // The exchange counts net per good (#121): a buy and a sale cancel out.
                  currency: {
                    ...p.currency,
                    cycleExchange: {
                      ...p.currency.cycleExchange,
                      [command.good]:
                        (p.currency.cycleExchange?.[command.good] ?? 0) + sign * q.gross,
                    },
                  },
                  goods: {
                    ...p.goods,
                    [command.good]: p.goods[command.good] + sign * command.quantity,
                  },
                }
              : p,
        ),
      };
    }
    case 'build':
    case 'upgrade': {
      if (state.pause) return reject('paused');
      const planet = player.planet;
      let target: { index: number } | { id: BuildingId; layer: SlotLayer };
      let building: BuildingId;
      let level: number;
      if (command.type === 'build') {
        if (!(BUILDING_IDS as readonly string[]).includes(command.building)) {
          return reject('unknown building');
        }
        if (command.layer !== 'surface' && command.layer !== 'orbit')
          return reject('unknown layer');
        const tech = BUILDINGS[command.building].requiresTech;
        if (tech !== null && !player.techs.includes(tech)) return reject('requires technology');
        const blocker = buildBlocker(
          planet,
          command.building,
          command.layer,
          techMods(player.techs),
        );
        if (blocker) return reject(blocker);
        target = { id: command.building, layer: command.layer };
        building = command.building;
        level = 1;
      } else {
        if (!Number.isInteger(command.index)) return reject('no such building');
        const blocker = upgradeBlocker(planet, command.index);
        if (blocker) return reject(blocker);
        const existing = planet.buildings[command.index]!;
        target = { index: command.index };
        building = existing.id;
        level = existing.level + 1;
      }
      // Prices are set in ГС and paid in the player's currency at the current
      // rate, so printed money buys only what it is worth.
      const gsCost = buildCost(building, level);
      const cost = { ...gsCost, cash: toLocal(gsCost.cash, player.currency.rate) };
      const unaffordable = canAfford(cost, player.cash, player.goods);
      if (unaffordable) return reject(unaffordable);

      const paid = payCost(player.cash, player.goods, cost);
      const readyTick = state.tick + BUILDINGS[building].buildCycles * BALANCE.cycleTicks;
      const nextPlanet = startConstruction(planet, target, readyTick);
      const index = 'index' in target ? target.index : nextPlanet.buildings.length - 1;
      events.push({ type: 'constructionStarted', playerId: player.id, index, building, level });
      return {
        ...state,
        players: state.players.map((p) =>
          p.id === player.id ? { ...p, ...paid, planet: nextPlanet } : p,
        ),
      };
    }
    case 'emit': {
      if (state.pause) return reject('paused');
      if (
        !Number.isInteger(command.amount) ||
        command.amount < 1 ||
        command.amount > CURRENCY.maxEmission
      ) {
        return reject(`amount must be 1–${CURRENCY.maxEmission}`);
      }
      // A reserve issuer exports half the inflation to everyone holding its money.
      const weight = state.reserve.issuer === player.id ? RESERVE.issuerInflationWeight : 1;
      const currency = emit(player.currency, player.planet.population, command.amount, weight);
      events.push({
        type: 'emitted',
        playerId: player.id,
        amount: command.amount,
        rate: currency.rate,
      });
      return {
        ...state,
        players: state.players.map((p) =>
          p.id === player.id ? { ...p, cash: roundMoney(p.cash + command.amount), currency } : p,
        ),
      };
    }
    case 'buildShip': {
      if (state.pause) return reject('paused');
      if (!isShipClass(command.ship)) return reject('unknown ship');
      const def = SHIP_CLASSES[command.ship];
      if (def.faction !== null && def.faction !== player.faction) return reject('other faction');
      if (def.requiresTech !== null && !player.techs.includes(def.requiresTech)) {
        return reject('requires technology');
      }
      if (parallelBuilds(player.planet) === 0) return reject('no spaceport');
      if (player.shipyard.length >= FLEET.maxQueue) return reject('queue full');
      const cash = toLocal(def.cost.cash, player.currency.rate);
      if (player.cash < cash) return reject('not enough cash');
      if (player.goods.alloys < def.cost.alloys) return reject('not enough alloys');
      events.push({ type: 'shipQueued', playerId: player.id, ship: command.ship });
      return {
        ...state,
        players: state.players.map((p) =>
          p.id === player.id
            ? {
                ...p,
                cash: roundMoney(p.cash - cash),
                goods: { ...p.goods, alloys: p.goods.alloys - def.cost.alloys },
                shipyard: [...p.shipyard, { ship: command.ship, ticksLeft: def.buildTicks }],
              }
            : p,
        ),
      };
    }
    case 'moveFleet': {
      if (state.pause) return reject('paused');
      const fleet = state.fleets.find((f) => f.id === command.fleetId);
      if (!fleet || fleet.owner !== player.id) return reject('no such fleet');
      const to = command.to;
      if (!to || !Number.isInteger(to.q) || !Number.isInteger(to.r)) return reject('bad hex');
      // Between two hexes (#125): it flies on to the next one and turns from there, so a
      // new order never pulls it back to the middle of the hex it left. Stopping turns it
      // back only if it is less than halfway.
      // An unarmed fleet flies around the enemy's ships (#158).
      const blocked = isArmed(fleet) ? undefined : enemyHexes(state, player.id);
      if (blocked?.has(hexKey(to))) return reject('enemy there');
      const next = fleet.path[0];
      if (next && fleet.progress > 0 && !blocked?.has(hexKey(next))) {
        const same = (a: Hex, b: Hex) => a.q === b.q && a.r === b.r;
        if (same(to, fleet.hex) && fleet.progress * 2 < fleetTicksPerHex(fleet)) {
          return {
            ...state,
            fleets: state.fleets.map((f) =>
              f.id === fleet.id ? { ...f, path: [], progress: 0, scan: null } : f,
            ),
          };
        }
        const onward =
          same(to, fleet.hex) || same(to, next) ? [] : findPath(state.system, next, to, blocked);
        if (!onward) return reject(blocked ? 'enemy in the way' : 'unreachable');
        return {
          ...state,
          fleets: state.fleets.map((f) =>
            f.id === fleet.id ? { ...f, path: [next, ...onward], scan: null } : f,
          ),
        };
      }
      const path = findPath(state.system, fleet.hex, to, blocked);
      if (!path)
        return reject(
          blocked && findPath(state.system, fleet.hex, to) ? 'enemy in the way' : 'unreachable',
        );
      return {
        ...state,
        fleets: state.fleets.map((f) =>
          f.id === fleet.id ? { ...f, path, progress: 0, scan: null } : f,
        ),
      };
    }
    case 'splitFleet': {
      if (state.pause) return reject('paused');
      const fleet = state.fleets.find((f) => f.id === command.fleetId);
      if (!fleet || fleet.owner !== player.id) return reject('no such fleet');
      const ids = new Set(command.shipIds);
      const moving = fleet.ships.filter((s) => ids.has(s.id));
      if (moving.length !== ids.size || moving.length === 0) return reject('no such ships');
      if (moving.length === fleet.ships.length) return reject('nothing would stay');
      const split: FleetState = {
        id: state.nextId,
        owner: player.id,
        hex: fleet.hex,
        ships: moving,
        path: [],
        progress: 0,
        scan: null,
      };
      return {
        ...state,
        nextId: state.nextId + 1,
        fleets: [
          ...state.fleets.map((f) =>
            f.id === fleet.id
              ? { ...f, ships: f.ships.filter((s) => !ids.has(s.id)), scan: null }
              : f,
          ),
          split,
        ],
      };
    }
    case 'scan': {
      if (state.pause) return reject('paused');
      const fleet = state.fleets.find((f) => f.id === command.fleetId);
      if (!fleet || fleet.owner !== player.id) return reject('no such fleet');
      if (!fleet.ships.some((s) => s.cls === 'explorer')) return reject('no explorer');
      if (fleet.path.length > 0) return reject('moving');
      const cloud = cloudAt(state.system, fleet.hex);
      if (!cloud) return reject('not in a dust cloud');
      const scanner = CLOUD_LEVELS[cloud.level].requiresTech;
      if (scanner !== null && !player.techs.includes(scanner)) return reject('requires technology');
      if ((state.exploration.findsLeft[cloud.id] ?? 0) <= 0) return reject('cloud exhausted');
      if (fleet.scan?.cloudId === cloud.id) return reject('already scanning');
      return {
        ...state,
        fleets: state.fleets.map((f) =>
          f.id === fleet.id ? { ...f, scan: { cloudId: cloud.id, progress: 0 } } : f,
        ),
      };
    }
    case 'setAutoTrade': {
      if (!techMods(player.techs).autoTrade) return reject('tech required');
      if (!isTradeGood(command.good)) return reject('unknown good');
      const rule = command.rule;
      if (
        rule &&
        (!['sell', 'buy'].includes(rule.mode) ||
          !(rule.keep >= 0) ||
          rule.keep > AUTO_TRADE.maxKeep)
      ) {
        return reject('bad rule');
      }
      const rules = { ...player.autoTrade };
      if (rule) rules[command.good] = { mode: rule.mode, keep: Math.round(rule.keep) };
      else delete rules[command.good];
      return {
        ...state,
        players: state.players.map((p) => (p.id === player.id ? { ...p, autoTrade: rules } : p)),
      };
    }
    case 'pickFind': {
      if (state.pause) return reject('paused');
      const offer = state.findOffers.find((o) => o.id === command.offerId);
      if (!offer || offer.playerId !== player.id) return reject('no such offer');
      const card = offer.cards[command.card];
      if (!card) return reject('no such card');
      const rng = new Rng(state.rng);
      const next = grantFind(
        { ...state, findOffers: state.findOffers.filter((o) => o.id !== offer.id) },
        player.id,
        offer.cloudId,
        offer.level,
        card,
        rng,
        events,
      );
      return { ...next, rng: rng.getState() };
    }
    case 'setStance': {
      if (state.pause) return reject('paused');
      const fleet = state.fleets.find((f) => f.id === command.fleetId);
      if (!fleet || fleet.owner !== player.id) return reject('no such fleet');
      if (!(STANCE_IDS as readonly string[]).includes(command.stance))
        return reject('unknown stance');
      const retreatAt = command.retreatAt ?? fleetRetreatAt(fleet);
      if (!(retreatAt >= 0 && retreatAt < 1)) return reject('retreatAt must be 0 to <1');
      return {
        ...state,
        fleets: state.fleets.map((f) =>
          f.id === fleet.id ? { ...f, stance: command.stance, retreatAt } : f,
        ),
      };
    }
    case 'buildOutpost': {
      if (state.pause) return reject('paused');
      const fleet = state.fleets.find((f) => f.id === command.fleetId);
      if (!fleet || fleet.owner !== player.id) return reject('no such fleet');
      const transport = fleet.ships.find((s) => s.cls === 'transport');
      if (!transport) return reject('no transport');
      if (fleet.path.length > 0) return reject('moving');
      const site = siteAt(outpostSites(state.system, state.exploration), fleet.hex);
      if (!site) return reject('no site here');
      if (state.outposts.some((o) => o.id === site.id)) return reject('site taken');
      const radius = OUTPOSTS[site.kind].zoneRadius;
      const contested = state.outposts.some(
        (o) =>
          o.owner !== player.id &&
          hexDistance(o.hex, site.hex) <= OUTPOSTS[o.kind].zoneRadius + radius,
      );
      if (contested) return reject('contested');
      const owned = state.outposts.filter((o) => o.owner === player.id).length;
      if (owned >= outpostLimit(spaceportLevel(player.planet))) return reject('outpost limit');
      const def = OUTPOSTS[site.kind];
      const cash = toLocal(def.cost.cash, player.currency.rate);
      if (player.cash < cash) return reject('not enough cash');
      if (player.goods.alloys < def.cost.alloys) return reject('not enough alloys');

      const ships = fleet.ships.filter((s) => s.id !== transport.id);
      const outpost: OutpostState = {
        id: site.id,
        owner: player.id,
        kind: site.kind,
        hex: site.hex,
        good: site.good,
        readyTick: state.tick + def.buildCycles * BALANCE.cycleTicks,
      };
      events.push({
        type: 'outpostFounded',
        playerId: player.id,
        outpostId: site.id,
        kind: site.kind,
      });
      return {
        ...state,
        outposts: [...state.outposts, outpost],
        fleets: ships.length
          ? state.fleets.map((f) => (f.id === fleet.id ? { ...f, ships, scan: null } : f))
          : state.fleets.filter((f) => f.id !== fleet.id),
        players: state.players.map((p) =>
          p.id === player.id
            ? {
                ...p,
                cash: roundMoney(p.cash - cash),
                goods: { ...p.goods, alloys: p.goods.alloys - def.cost.alloys },
              }
            : p,
        ),
      };
    }
    case 'declareWar': {
      if (state.pause) return reject('paused');
      const target = state.players.find((p) => p.id === command.target);
      if (!target || target.id === player.id) return reject('no such player');
      if (atWar(state, player.id, target.id)) return reject('already at war');
      if (state.pendingWars.some((w) => pairIs({ a: w.by, b: w.target }, player.id, target.id))) {
        return reject('war already declared');
      }
      if (player.vassalOf === target.id) return reject('overlord');
      if (target.vassalOf === player.id) return reject('vassal');
      if (hasAgreement(state.agreements, player.id, target.id, 'alliance')) return reject('allied');
      const brokePact = hasAgreement(state.agreements, player.id, target.id, 'nonAggression');
      const startsAt = state.tick + DIPLOMACY.warDelayTicks;
      events.push({ type: 'warDeclared', by: player.id, target: target.id, startsAt });
      // War tears up signed contracts between the two; the declarer answers for it.
      let torn: GameState = state;
      for (const c of state.contracts) {
        if (c.status === 'active' && pairIs({ a: c.seller, b: c.buyer }, player.id, target.id)) {
          torn = breakContract(torn, c, player.id, events);
        }
      }
      state = torn;
      for (const a of state.agreements.filter((x) => pairIs(x, player.id, target.id))) {
        events.push({ type: 'agreementEnded', kind: a.kind, a: a.a, b: a.b });
      }
      return {
        ...state,
        pendingWars: [...state.pendingWars, { by: player.id, target: target.id, startsAt }],
        // War wipes out the trade route the two had built (#74).
        tradeRoutes: state.tradeRoutes.filter((r) => !pairIs(r, player.id, target.id)),
        agreements: state.agreements.filter((x) => !pairIs(x, player.id, target.id)),
        proposals: state.proposals.filter(
          (x) => !pairIs({ a: x.from, b: x.to }, player.id, target.id),
        ),
        players: brokePact
          ? state.players.map((p) =>
              p.id === player.id
                ? { ...p, reputation: Math.max(0, p.reputation - DIPLOMACY.brokenPactPenalty) }
                : p,
            )
          : state.players,
      };
    }
    case 'propose':
    case 'accept': {
      if (state.pause) return reject('paused');
      const other = state.players.find((p) => p.id === command.other);
      if (!other || other.id === player.id) return reject('no such player');
      const kind = command.kind;
      if (kind !== 'peace' && !(AGREEMENT_IDS as readonly string[]).includes(kind)) {
        return reject('unknown agreement');
      }
      const warring = atWar(state, player.id, other.id);
      if (kind === 'peace' && !warring) return reject('not at war');
      if (kind !== 'peace' && warring) return reject('at war');
      if (kind !== 'peace' && hasAgreement(state.agreements, player.id, other.id, kind)) {
        return reject('already agreed');
      }
      if (command.type === 'propose') {
        if (
          state.proposals.some((x) => x.kind === kind && x.from === player.id && x.to === other.id)
        ) {
          return reject('already proposed');
        }
        events.push({ type: 'proposed', kind, a: player.id, b: other.id });
        return {
          ...state,
          proposals: [...state.proposals, { kind, from: player.id, to: other.id }],
        };
      }
      const offer = state.proposals.find(
        (x) => x.kind === kind && x.from === other.id && x.to === player.id,
      );
      if (!offer) return reject('nothing to accept');
      const proposals = state.proposals.filter((x) => x !== offer);
      events.push({ type: 'agreed', kind, a: other.id, b: player.id });
      if (kind === 'peace') {
        events.push({ type: 'peaceMade', a: other.id, b: player.id });
        return {
          ...state,
          proposals,
          wars: state.wars.filter((w) => !pairIs(w, player.id, other.id)),
          raids: state.raids.filter(
            (r) => !pairIs({ a: r.owner, b: r.victim }, player.id, other.id),
          ),
          sieges: state.sieges.filter(
            (r) => !pairIs({ a: r.owner, b: r.victim }, player.id, other.id),
          ),
        };
      }
      return {
        ...state,
        proposals,
        agreements: [...state.agreements, { kind, a: other.id, b: player.id }],
      };
    }
    case 'cancelAgreement': {
      if (state.pause) return reject('paused');
      const found = state.agreements.find(
        (x) => x.kind === command.kind && pairIs(x, player.id, command.other),
      );
      if (!found) return reject('no such agreement');
      if (found.kind === 'alliance' && sameTeam(state.players, player.id, command.other)) {
        return reject('teammates');
      }
      events.push({ type: 'agreementEnded', kind: found.kind, a: found.a, b: found.b });
      return { ...state, agreements: state.agreements.filter((x) => x !== found) };
    }
    case 'raid': {
      if (state.pause) return reject('paused');
      const fleet = state.fleets.find((f) => f.id === command.fleetId);
      if (!fleet || fleet.owner !== player.id) return reject('no such fleet');
      if (!isArmed(fleet)) return reject('no warships');
      if (fleet.path.length > 0) return reject('moving');
      if (state.raids.some((r) => r.fleetId === fleet.id)) return reject('already raiding');
      const key = hexKey(fleet.hex);
      const capital = state.players.find(
        (p) => p.id !== player.id && hexKey(capitalHex(state, p.id)) === key,
      );
      const outpost = state.outposts.find((o) => o.owner !== player.id && hexKey(o.hex) === key);
      const caravanHere = state.contracts.find((c) => {
        if (!c.caravan || cargoTotal(c.caravan.cargo) <= 0) return false;
        const cf = state.fleets.find((f) => f.id === c.caravan!.fleetId);
        return !!cf && cf.owner !== player.id && hexKey(cf.hex) === key;
      });
      const caravanOwner = caravanHere
        ? state.fleets.find((f) => f.id === caravanHere.caravan!.fleetId)!.owner
        : undefined;
      const victim =
        capital ?? state.players.find((p) => p.id === (outpost?.owner ?? caravanOwner));
      if (!victim) return reject('nothing to raid');
      if (victim.vassalOf === player.id || player.vassalOf === victim.id) return reject('vassal');
      const defenders = state.fleets.some(
        (f) => f.owner === victim.id && hexKey(f.hex) === key && isArmed(f),
      );
      if (defenders) return reject('defended');
      if (capital && fleetDamage(fleet) <= planetGuns(capital.planet))
        return reject('defences too strong');
      let next = state;
      if (!atWar(state, player.id, victim.id)) {
        // Only the Reforged raid in peacetime — and pay for it (GDD §8).
        if (player.faction !== 'reforged') return reject('not at war');
        if (hasAgreement(state.agreements, player.id, victim.id, 'alliance'))
          return reject('allied');
        events.push({ type: 'peacetimeRaid', playerId: player.id, victim: victim.id });
        next = {
          ...next,
          wars: [...next.wars, { a: player.id, b: victim.id, since: state.tick }],
          tradeRoutes: next.tradeRoutes.filter((r) => !pairIs(r, player.id, victim.id)),
          agreements: next.agreements.filter((x) => !pairIs(x, player.id, victim.id)),
          pendingWars: next.pendingWars.filter(
            (w) => !pairIs({ a: w.by, b: w.target }, player.id, victim.id),
          ),
          players: next.players.map((p) =>
            p.id === player.id
              ? { ...p, reputation: Math.max(0, p.reputation - DIPLOMACY.peacetimeRaidPenalty) }
              : p,
          ),
        };
        events.push({ type: 'warStarted', a: player.id, b: victim.id });
      }
      if (!capital && !outpost && caravanHere) {
        // A caravan cannot be held for 20 s: the raider strips it on the spot.
        return robCaravan(next, caravanHere, player.id, raidCapacity(player, fleet), events);
      }
      events.push({
        type: 'raidStarted',
        playerId: player.id,
        victim: victim.id,
        fleetId: fleet.id,
      });
      const raid: Raid = {
        fleetId: fleet.id,
        owner: player.id,
        victim: victim.id,
        target: capital ? { kind: 'planet' } : { kind: 'outpost', id: outpost!.id },
        progress: 0,
      };
      return { ...next, raids: [...next.raids, raid] };
    }
    case 'invade': {
      if (state.pause) return reject('paused');
      if (!player.techs.includes(INVASION.requiresTech)) return reject('requires technology');
      const fleet = state.fleets.find((f) => f.id === command.fleetId);
      if (!fleet || fleet.owner !== player.id) return reject('no such fleet');
      const key = hexKey(fleet.hex);
      const victim = state.players.find(
        (p) => p.id !== player.id && hexKey(capitalHex(state, p.id)) === key,
      );
      if (!victim) return reject('not at an enemy capital');
      if (!atWar(state, player.id, victim.id)) return reject('not at war');
      if (state.sieges.some((x) => x.victim === victim.id)) return reject('already under siege');
      const landers = fleet.ships.filter((s) => s.cls === 'lander');
      if (!landers.length) return reject('no landers');
      const defenders = state.fleets.some(
        (f) => f.owner === victim.id && hexKey(f.hex) === key && isArmed(f),
      );
      if (defenders) return reject('no orbital control');
      const troops = landers.length * INVASION.troopsPerLander;
      if (troops <= garrisonStrength(victim.planet)) return reject('garrison too strong');
      const left = fleet.ships.filter((s) => s.cls !== 'lander');
      events.push({ type: 'siegeStarted', playerId: player.id, victim: victim.id });
      return {
        ...state,
        sieges: [...state.sieges, { owner: player.id, victim: victim.id, troops, progress: 0 }],
        fleets: left.length
          ? state.fleets.map((f) => (f.id === fleet.id ? { ...f, ships: left } : f))
          : state.fleets.filter((f) => f.id !== fleet.id),
      };
    }
    case 'declareIndependence': {
      if (state.pause) return reject('paused');
      const overlord = state.players.find((p) => p.id === player.vassalOf);
      if (!overlord) return reject('not a vassal');
      const key = hexKey(capitalHex(state, player.id));
      const occupiers = state.fleets
        .filter((f) => f.owner === overlord.id && hexKey(f.hex) === key)
        .reduce((sum, f) => sum + fleetDamage(f) * INVASION.fleetStrengthPerDamage, 0);
      const failed =
        overlord.failedCrisisAt !== null &&
        state.tick - overlord.failedCrisisAt <= INVASION.crisisFailureWindowTicks;
      if (garrisonStrength(player.planet) <= occupiers && !failed)
        return reject('too weak to rebel');
      events.push({ type: 'independence', vassal: player.id, overlord: overlord.id });
      events.push({ type: 'warStarted', a: player.id, b: overlord.id });
      return {
        ...state,
        // Independence is fought for: the war with the old overlord starts at once.
        wars: [...state.wars, { a: player.id, b: overlord.id, since: state.tick }],
        players: state.players.map((p) => (p.id === player.id ? { ...p, vassalOf: null } : p)),
      };
    }
    case 'proposeContract': {
      if (state.pause) return reject('paused');
      const other = state.players.find((p) => p.id === command.other);
      if (!other || other.id === player.id) return reject('no such player');
      if (atWar(state, player.id, other.id)) return reject('at war');
      if (!isTradeGood(command.good)) return reject('unknown good');
      const n = command.quantity;
      if (!Number.isInteger(n) || n < 1 || n > MARKET.maxOrderUnits) return reject('bad quantity');
      const d = command.deliveries;
      if (!Number.isInteger(d) || d < 1 || d > TRADE.maxDeliveries) return reject('bad deliveries');
      const barter = command.barter ?? null;
      const price = command.price ?? null;
      if ((price === null) === (barter === null)) return reject('price or barter');
      if (price !== null && !(price > 0 && Number.isFinite(price))) return reject('bad price');
      if (
        barter &&
        (!isTradeGood(barter.good) ||
          !Number.isInteger(barter.quantity) ||
          barter.quantity < 1 ||
          barter.quantity > MARKET.maxOrderUnits)
      ) {
        return reject('bad barter');
      }
      const mine = state.contracts.filter((c) => involves(c, player.id)).length;
      if (mine >= contractSlots(player.planet, techMods(player.techs))) {
        return reject('no contract slot');
      }
      const seller = command.role === 'sell' ? player.id : other.id;
      const buyer = command.role === 'sell' ? other.id : player.id;
      const blocked = contractBlock(state.levers, seller, buyer);
      if (blocked) return reject(blocked);
      const contract: Contract = {
        id: state.nextId,
        seller,
        buyer,
        proposedBy: player.id,
        status: 'proposed',
        good: command.good,
        quantity: n,
        price,
        barter,
        deliveriesLeft: d,
        missed: 0,
        caravan: null,
      };
      events.push({ type: 'contractProposed', contractId: contract.id, seller, buyer });
      return { ...state, nextId: state.nextId + 1, contracts: [...state.contracts, contract] };
    }
    case 'acceptContract': {
      if (state.pause) return reject('paused');
      const c = state.contracts.find((x) => x.id === command.contractId);
      if (!c || c.status !== 'proposed' || !involves(c, player.id) || c.proposedBy === player.id) {
        return reject('nothing to accept');
      }
      if (atWar(state, c.seller, c.buyer)) return reject('at war');
      const blocked = contractBlock(state.levers, c.seller, c.buyer);
      if (blocked) return reject(blocked);
      const mine = state.contracts.filter((x) => x.status === 'active' && involves(x, player.id));
      if (mine.length >= contractSlots(player.planet, techMods(player.techs))) {
        return reject('no contract slot');
      }
      events.push({ type: 'contractSigned', contractId: c.id, seller: c.seller, buyer: c.buyer });
      return {
        ...state,
        contracts: state.contracts.map((x) => (x.id === c.id ? { ...x, status: 'active' } : x)),
      };
    }
    case 'cancelContract': {
      if (state.pause) return reject('paused');
      const c = state.contracts.find((x) => x.id === command.contractId);
      if (!c || !involves(c, player.id)) return reject('no such contract');
      // Withdrawing an offer is free; walking out of a signed contract is a breach (GDD §6).
      return c.status === 'proposed'
        ? { ...state, contracts: state.contracts.filter((x) => x.id !== c.id) }
        : breakContract(state, c, player.id, events);
    }
    case 'assignCaravan': {
      if (state.pause) return reject('paused');
      const c = state.contracts.find((x) => x.id === command.contractId);
      if (!c || c.status !== 'active' || c.seller !== player.id) return reject('no such contract');
      if (c.caravan) return reject('caravan already assigned');
      const fleet = state.fleets.find((f) => f.id === command.fleetId);
      if (!fleet || fleet.owner !== player.id) return reject('no such fleet');
      if (state.contracts.some((x) => x.caravan?.fleetId === fleet.id)) return reject('fleet busy');
      const cargo = caravanCargo(state, c, fleet);
      const needs = Math.max(c.quantity, c.barter?.quantity ?? 0);
      if (cargo < needs) return reject('not enough cargo');
      const home = capitalHex(state, player.id);
      const path = findPath(state.system, fleet.hex, home) ?? [];
      const caravan: Caravan = { fleetId: fleet.id, phase: 'atSeller', cargo: {} };
      return {
        ...state,
        contracts: state.contracts.map((x) => (x.id === c.id ? { ...x, caravan } : x)),
        fleets: state.fleets.map((f) =>
          f.id === fleet.id ? { ...f, path, progress: 0, scan: null } : f,
        ),
      };
    }
    case 'useLever': {
      if (state.pause) return reject('paused');
      if (!(LEVER_IDS as readonly string[]).includes(command.lever)) return reject('unknown lever');
      const kind = command.lever;
      const def = LEVERS[kind];
      if (!player.techs.includes(def.requiresTech)) return reject('tech required');
      if (player.influence < def.cost) return reject('not enough influence');
      const lasts = def.cycles * BALANCE.cycleTicks;
      let next: GameState = state;
      let target: string | null = null;
      let good: TradeGoodId | null = null;
      let status: Lever['status'] = 'active';
      if (kind === 'embargo' || kind === 'sanctions') {
        const other = state.players.find((p) => p.id === command.target);
        if (!other || other.id === player.id) return reject('no such player');
        if (sameTeam(state.players, player.id, other.id)) return reject('teammates');
        target = other.id;
        // One coalition per target; one embargo per pair.
        const taken = state.levers.some(
          (l) =>
            l.kind === kind &&
            l.target === other.id &&
            (kind === 'sanctions' || l.by.includes(player.id)),
        );
        if (taken) return reject('already in force');
        // Sanctions need a coalition, unless the reserve issuer imposes them alone;
        // in a duel the only rival is coalition enough.
        if (
          kind === 'sanctions' &&
          state.reserve.issuer !== player.id &&
          sanctionsBackers(state) > 1
        ) {
          status = 'gathering';
        }
      } else if (kind === 'tariff') {
        if (state.levers.some((l) => l.kind === 'tariff' && l.by.includes(player.id))) {
          return reject('already in force');
        }
      } else {
        // Dumping: an ordinary sale on the exchange, then the price is held down.
        if (command.good === undefined || command.quantity === undefined) {
          return reject('good and quantity');
        }
        const sale: GameEvent[] = [];
        const sold = applyCommand(
          state,
          {
            type: 'marketSell',
            playerId: player.id,
            good: command.good,
            quantity: command.quantity,
          },
          sale,
        );
        const rejected = sale.find((e) => e.type === 'commandRejected');
        if (rejected?.type === 'commandRejected') return reject(rejected.reason);
        events.push(...sale);
        good = command.good;
        next = {
          ...sold,
          market: shockPrice(
            sold.market,
            good,
            LEVER_EFFECTS.dumpingPriceFactor,
            state.tick + lasts,
          ),
        };
      }
      const lever: Lever = {
        id: next.nextId,
        kind,
        by: [player.id],
        target,
        good,
        until: state.tick + (status === 'gathering' ? BALANCE.cycleTicks : lasts),
        status,
      };
      events.push({
        type: 'leverUsed',
        playerId: player.id,
        leverId: lever.id,
        lever: kind,
        target,
        good,
      });
      if (kind === 'sanctions' && status === 'active') {
        events.push({ type: 'sanctionsImposed', leverId: lever.id, target: target!, by: lever.by });
      }
      return {
        ...next,
        nextId: next.nextId + 1,
        levers: [...next.levers, lever],
        players: next.players.map((p) =>
          p.id === player.id ? { ...p, influence: p.influence - def.cost } : p,
        ),
      };
    }
    case 'joinSanctions': {
      if (state.pause) return reject('paused');
      const l = state.levers.find((x) => x.id === command.leverId);
      if (!l || l.kind !== 'sanctions' || l.status !== 'gathering')
        return reject('nothing to join');
      if (l.by.includes(player.id) || l.target === player.id) return reject('cannot join');
      if (sameTeam(state.players, player.id, l.target!)) return reject('teammates');
      const cost = LEVERS.sanctions.cost;
      if (player.influence < cost) return reject('not enough influence');
      const by = [...l.by, player.id];
      const ready = by.length >= sanctionsBackers(state) || state.reserve.issuer === player.id;
      events.push({ type: 'sanctionsJoined', playerId: player.id, leverId: l.id });
      if (ready) events.push({ type: 'sanctionsImposed', leverId: l.id, target: l.target!, by });
      const joined: Lever = ready
        ? {
            ...l,
            by,
            status: 'active',
            until: state.tick + LEVERS.sanctions.cycles * BALANCE.cycleTicks,
          }
        : { ...l, by };
      return {
        ...state,
        levers: state.levers.map((x) => (x.id === l.id ? joined : x)),
        players: state.players.map((p) =>
          p.id === player.id ? { ...p, influence: p.influence - cost } : p,
        ),
      };
    }
    case 'contributeProject': {
      if (state.pause) return reject('paused');
      const project = state.projects.find((x) => x.id === command.projectId);
      if (!project || state.tick >= project.deadline) return reject('no such project');
      if (projectTotal(project) >= project.full - 1e-9) return reject('project complete');
      const prev = project.byPlayer[player.id] ?? emptyContribution;
      let added: Contribution;
      let next: GameState = state;
      const n = command.quantity ?? 0;
      if (command.what === 'science') {
        if (!Number.isInteger(n) || n < 1 || n > player.planet.science)
          return reject('bad quantity');
        added = { ...prev, points: prev.points + scienceValue(n), science: prev.science + n };
        next = updatePlayer(next, player.id, (p) => ({
          ...p,
          planet: { ...p.planet, science: p.planet.science - n },
        }));
      } else if (command.what === 'fleet') {
        const fleet = state.fleets.find((f) => f.id === command.fleetId);
        if (!fleet || fleet.owner !== player.id) return reject('no such fleet');
        if (hexKey(fleet.hex) !== hexKey(project.hex)) return reject('not at the project');
        if (state.contracts.some((c) => c.caravan?.fleetId === fleet.id)) return reject('caravan');
        const ships = fleet.ships.map((x) => x.cls);
        const value = ships.reduce((sum, cls) => sum + shipValue(cls), 0);
        added = { ...prev, points: prev.points + value, ships: [...prev.ships, ...ships] };
        next = { ...next, fleets: next.fleets.filter((f) => f.id !== fleet.id) };
      } else {
        const good = command.what;
        if (!isTradeGood(good) || !PROJECTS[project.kind].goods.includes(good)) {
          return reject('not taken here');
        }
        if (!Number.isInteger(n) || n < 1 || n > MARKET.maxOrderUnits)
          return reject('bad quantity');
        if (player.goods[good] < n) return reject('not enough goods');
        added = {
          ...prev,
          points: prev.points + goodsValue(good, n),
          goods: { ...prev.goods, [good]: (prev.goods[good] ?? 0) + n },
        };
        next = updatePlayer(next, player.id, (p) => ({
          ...p,
          goods: { ...p.goods, [good]: p.goods[good] - n },
        }));
      }
      const updated = { ...project, byPlayer: { ...project.byPlayer, [player.id]: added } };
      events.push({
        type: 'projectContribution',
        projectId: project.id,
        playerId: player.id,
        points: added.points - prev.points,
        total: projectTotal(updated),
      });
      return {
        ...next,
        projects: next.projects.map((x) => (x.id === project.id ? updated : x)),
      };
    }
    case 'buildMegastructure': {
      if (state.pause) return reject('paused');
      if (!techMods(player.techs).megastructure) return reject('requires technology');
      if (state.megastructures.some((m) => m.owner === player.id))
        return reject('already building');
      const fleet = state.fleets.find((f) => f.id === command.fleetId);
      if (!fleet || fleet.owner !== player.id) return reject('no such fleet');
      const transport = fleet.ships.find((x) => x.cls === 'transport');
      if (!transport) return reject('no transport');
      const key = hexKey(fleet.hex);
      if (!state.system.coronaHexes.some((h) => hexKey(h) === key))
        return reject('not in the corona');
      if (state.megastructures.some((m) => hexKey(m.hex) === key)) return reject('site taken');
      const cost = MEGASTRUCTURE.stageCost;
      if (player.goods.alloys < cost.alloys || player.goods.exotics < cost.exotics) {
        return reject('not enough goods');
      }
      const ships = fleet.ships.filter((x) => x.id !== transport.id);
      events.push({ type: 'megastructureStarted', playerId: player.id, stages: 0 });
      return {
        ...updatePlayer(state, player.id, (p) => ({
          ...p,
          goods: {
            ...p.goods,
            alloys: p.goods.alloys - cost.alloys,
            exotics: p.goods.exotics - cost.exotics,
          },
        })),
        fleets: ships.length
          ? state.fleets.map((f) => (f.id === fleet.id ? { ...f, ships } : f))
          : state.fleets.filter((f) => f.id !== fleet.id),
        megastructures: [
          ...state.megastructures,
          {
            owner: player.id,
            hex: fleet.hex,
            stages: 0,
            buildingUntil: state.tick + MEGASTRUCTURE.stageTicks,
            hold: null,
          },
        ],
      };
    }
    case 'startResearch': {
      if (state.pause) return reject('paused');
      if (!isTech(command.tech)) return reject('unknown technology');
      const tech = command.tech;
      const r = player.research;
      if (r.active.includes(tech)) return reject('already researching');
      const blocked = researchBlocker(tech, player.techs);
      if (blocked) return reject(blocked);
      if (r.active.length >= researchSlots(player.planet)) return reject('no research slot');
      // Exotics are paid once, on the first start (GDD §10: tier 3 and the final).
      const exotics = r.exoticsPaid.includes(tech) ? 0 : RESEARCH.cost[TECHS[tech].tier].exotics;
      if (player.goods.exotics < exotics) return reject('not enough exotics');
      events.push({ type: 'researchStarted', playerId: player.id, tech });
      return {
        ...state,
        players: state.players.map((p) =>
          p.id === player.id
            ? {
                ...p,
                goods: { ...p.goods, exotics: p.goods.exotics - exotics },
                research: {
                  ...r,
                  active: [...r.active, tech],
                  exoticsPaid: exotics > 0 ? [...r.exoticsPaid, tech] : r.exoticsPaid,
                },
              }
            : p,
        ),
      };
    }
    case 'cancelResearch': {
      if (state.pause) return reject('paused');
      const r = player.research;
      if (!isTech(command.tech) || !r.active.includes(command.tech)) {
        return reject('not researching');
      }
      events.push({ type: 'researchCancelled', playerId: player.id, tech: command.tech });
      return {
        ...state,
        players: state.players.map((p) =>
          p.id === player.id
            ? { ...p, research: { ...r, active: r.active.filter((t) => t !== command.tech) } }
            : p,
        ),
      };
    }
    case 'transfer': {
      if (state.pause) return reject('paused');
      const to = state.players.find((p) => p.id === command.to);
      if (!to || to.id === player.id) return reject('no such player');
      // No king-making (GDD §12): gifts stop while someone else counts down to victory.
      if (state.countdowns.some((c) => c.playerId !== player.id))
        return reject('victory countdown');
      const amount = command.amount;
      if (!(amount > 0 && Number.isFinite(amount))) return reject('bad amount');
      const arrived = amount * (1 - TRADE.transferTax);
      if (command.what === 'cash') {
        if (player.cash < amount) return reject('not enough cash');
        const gs = arrived * player.currency.rate;
        events.push({
          type: 'transferred',
          from: player.id,
          to: to.id,
          what: 'cash',
          sent: amount,
          arrived: gs,
        });
        return {
          ...state,
          players: state.players.map((p) =>
            p.id === player.id
              ? { ...p, cash: roundMoney(p.cash - amount) }
              : p.id === to.id
                ? { ...p, cash: roundMoney(p.cash + gs / p.currency.rate) }
                : p,
          ),
        };
      }
      const good = command.what;
      if (!isTradeGood(good)) return reject('unknown good');
      if (player.goods[good] < amount) return reject('not enough goods');
      events.push({
        type: 'transferred',
        from: player.id,
        to: to.id,
        what: good,
        sent: amount,
        arrived,
      });
      return {
        ...state,
        players: state.players.map((p) =>
          p.id === player.id
            ? { ...p, goods: { ...p.goods, [good]: p.goods[good] - amount } }
            : p.id === to.id
              ? { ...p, goods: { ...p.goods, [good]: p.goods[good] + arrived } }
              : p,
        ),
      };
    }
    case 'mergeFleets': {
      if (state.pause) return reject('paused');
      const ids = [...new Set(command.fleetIds)];
      const fleets = ids.map((id) => state.fleets.find((f) => f.id === id));
      if (fleets.length < 2) return reject('need two fleets');
      if (fleets.some((f) => !f || f.owner !== player.id)) return reject('no such fleet');
      const [head, ...rest] = fleets as FleetState[];
      if (rest.some((f) => f.hex.q !== head!.hex.q || f.hex.r !== head!.hex.r)) {
        return reject('not in one hex');
      }
      // Fleets come with «Логистика флота» (#160), up to four ships.
      if (!techMods(player.techs).formations) return reject('needs fleet logistics');
      const ships = (fleets as FleetState[]).reduce((n, f) => n + f.ships.length, 0);
      if (ships > FORMATION.maxShips) return reject('fleet full');
      const gone = new Set(rest.map((f) => f.id));
      return {
        ...state,
        fleets: state.fleets
          .filter((f) => !gone.has(f.id))
          .map((f) =>
            f.id === head!.id
              ? {
                  ...f,
                  ships: [...f.ships, ...rest.flatMap((r) => r.ships)],
                  path: [],
                  progress: 0,
                  scan: null,
                }
              : f,
          ),
      };
    }
  }
}

/** One tick of every spaceport: finished ships appear as new fleets at the capital. */
function runShipyards(state: GameState, events: GameEvent[]): GameState {
  if (state.players.every((p) => p.shipyard.length === 0)) return state;
  let nextId = state.nextId;
  const born: FleetState[] = [];
  const players = state.players.map((p) => {
    if (p.shipyard.length === 0) return p;
    const parallel = parallelBuilds(p.planet);
    const shipyard: BuildOrder[] = [];
    // Ships born this tick count too: two corvettes at once are «Корвет» and «Корвет 2».
    const numbered = (cls: ShipClassId) => {
      const n = freeShipNumber([...state.fleets, ...born], p.id, cls);
      return n > 1 ? { n } : {};
    };
    p.shipyard.forEach((order, i) => {
      const ticksLeft = i < parallel ? order.ticksLeft - 1 : order.ticksLeft;
      if (ticksLeft > 0) {
        shipyard.push({ ...order, ticksLeft });
        return;
      }
      const fleet: FleetState = {
        id: nextId + 1,
        owner: p.id,
        hex: capitalHex(state, p.id),
        ships: [{ id: nextId, cls: order.ship, ...numbered(order.ship) }],
        path: [],
        progress: 0,
        scan: null,
      };
      nextId += 2;
      born.push(fleet);
      events.push({ type: 'shipBuilt', playerId: p.id, ship: order.ship, fleetId: fleet.id });
    });
    return { ...p, shipyard };
  });
  return {
    ...state,
    players,
    nextId,
    fleets: born.length ? [...state.fleets, ...born] : state.fleets,
  };
}

/**
 * One tick of every scan (GDD §7). The fleet that finishes first takes the
 * next find of its cloud; it keeps scanning until the cloud is empty.
 */
function runScans(state: GameState, events: GameEvent[]): GameState {
  if (state.fleets.every((f) => f.scan === null)) return state;
  let next = state;
  const rng = new Rng(state.rng);
  // Fleets in id order, so ties for the last find always go the same way.
  for (const fleet of [...state.fleets].sort((a, b) => a.id - b.id)) {
    if (!fleet.scan) continue;
    const cloud = state.system.clouds.find((c) => c.id === fleet.scan!.cloudId)!;
    const progress = fleet.scan.progress + 1;
    const scanner = state.players.find((p) => p.id === fleet.owner);
    const scanTime = scanner ? techMods(scanner.techs).scanTime : 1;
    const done = progress >= Math.ceil(CLOUD_LEVELS[cloud.level].scanTicks * scanTime);
    const left = next.exploration.findsLeft[cloud.id] ?? 0;
    let scan: FleetState['scan'] = left > 0 ? { cloudId: cloud.id, progress } : null;
    if (done && left > 0) {
      next = applyFind(next, fleet.owner, cloud.id, cloud.level, rng, events);
      const remaining = next.exploration.findsLeft[cloud.id] ?? 0;
      scan = remaining > 0 ? { cloudId: cloud.id, progress: 0 } : null;
      if (remaining === 0) events.push({ type: 'cloudExhausted', cloudId: cloud.id });
    }
    next = {
      ...next,
      fleets: next.fleets.map((f) => (f.id === fleet.id ? { ...f, scan } : f)),
    };
  }
  return { ...next, rng: rng.getState() };
}

/**
 * Draws the next find of a cloud for `playerId`. Pirates wake at once; any
 * other find is granted at once, or — with the find choice on (#105) —
 * offered as cards to pick one of.
 */
function applyFind(
  state: GameState,
  playerId: string,
  cloudId: string,
  level: CloudLevelId,
  rng: Rng,
  events: GameEvent[],
): GameState {
  const drawn = drawFind(state.exploration, level, rng);
  const exploration: ExplorationState = {
    ...drawn.exploration,
    findsLeft: {
      ...drawn.exploration.findsLeft,
      [cloudId]: (drawn.exploration.findsLeft[cloudId] ?? 0) - 1,
    },
  };
  if (drawn.find === 'pirates') {
    const cloud = state.system.clouds.find((c) => c.id === cloudId)!;
    const warning: PirateWarning = {
      cloudId,
      hex: cloud.center,
      level,
      spawnTick: state.tick + PIRATE_RULES.warningTicks,
    };
    events.push({
      type: 'piratesWarning',
      playerId,
      cloudId,
      hex: cloud.center,
      inTicks: PIRATE_RULES.warningTicks,
    });
    events.push({ type: 'found', playerId, cloudId, find: 'pirates', gains: { pirates: level } });
    return { ...state, exploration, pirateWarnings: [...state.pirateWarnings, warning] };
  }
  const first = cardOf(drawn.find, rng);
  if (!state.findChoice) {
    return grantFind({ ...state, exploration }, playerId, cloudId, level, first, rng, events);
  }
  const cloud = state.system.clouds.find((c) => c.id === cloudId)!;
  const offer: FindOffer = {
    id: state.nextId,
    playerId,
    cloudId,
    level,
    cards: [first, ...moreCards(state.system, exploration, cloud, first, rng)],
    expiresAt: state.tick + FIND_CHOICE.timeoutTicks,
  };
  events.push({ type: 'findOffered', playerId, offerId: offer.id, cloudId });
  return {
    ...state,
    exploration,
    nextId: state.nextId + 1,
    findOffers: [...state.findOffers, offer],
  };
}

/** A find as a card: a cache names its good now, so the card says what it holds. */
function cardOf(find: Exclude<FindId, 'pirates'>, rng: Rng): FindCard {
  return find === 'cache' ? { find, good: rng.pick(FINDS.cache).good } : { find };
}

const sameCard = (a: FindCard, b: FindCard) => a.find === b.find && a.good === b.good;

/**
 * The other cards of an offer (#105): different finds this cloud's level can
 * hold, pirates aside; an asteroid only while the cloud has room for one.
 */
function moreCards(
  system: StarSystem,
  exploration: ExplorationState,
  cloud: DustCloud,
  first: FindCard,
  rng: Rng,
): FindCard[] {
  const bag = CLOUD_LEVELS[cloud.level].bag;
  const pool: FindCard[] = [];
  if (bag.cache > 0) for (const c of FINDS.cache) pool.push({ find: 'cache', good: c.good });
  if (bag.derelict > 0) pool.push({ find: 'derelict' });
  if (bag.anomaly > 0) pool.push({ find: 'anomaly' });
  if (bag.asteroid > 0 && asteroidHex(system, exploration, cloud)) pool.push({ find: 'asteroid' });
  return rng.shuffle(pool.filter((c) => !sameCard(c, first))).slice(0, FIND_CHOICE.cards - 1);
}

/** Hands a find card to `playerId`: goods, science, a fragment or an asteroid site. */
function grantFind(
  state: GameState,
  playerId: string,
  cloudId: string,
  level: CloudLevelId,
  card: FindCard,
  rng: Rng,
  events: GameEvent[],
): GameState {
  let exploration = state.exploration;
  const gains: Record<string, number | string> = {};
  let players = state.players;
  const give = (update: (p: PlayerState) => PlayerState) => {
    players = players.map((p) => (p.id === playerId ? update(p) : p));
  };
  switch (card.find) {
    case 'cache': {
      const loot = FINDS.cache.find((c) => c.good === card.good) ?? FINDS.cache[0]!;
      const finder = players.find((p) => p.id === playerId);
      const yieldFactor =
        loot.good === 'exotics' && finder ? techMods(finder.techs).exoticsYield : 1;
      const amount = loot.amount * level * yieldFactor;
      gains[loot.good] = amount;
      give((p) => ({ ...p, goods: { ...p.goods, [loot.good]: p.goods[loot.good] + amount } }));
      break;
    }
    case 'derelict': {
      const metals = FINDS.derelict.metals * level;
      gains.metals = metals;
      gains.fragments = FINDS.derelict.fragments;
      give((p) => {
        // Each fragment takes a share off a random unknown tier-1–2 technology (GDD §7).
        const discounts = { ...p.research.discounts };
        for (let i = 0; i < FINDS.derelict.fragments; i++) {
          const targets = fragmentTargets({ ...p.research, discounts }, p.techs);
          if (targets.length === 0) break;
          const tech = rng.pick(targets);
          discounts[tech] = Math.min(
            RESEARCH.maxFragmentDiscount,
            (discounts[tech] ?? 0) + RESEARCH.fragmentDiscount,
          );
          gains.fragmentTech = tech;
        }
        return {
          ...p,
          goods: { ...p.goods, metals: p.goods.metals + metals },
          techFragments: p.techFragments + FINDS.derelict.fragments,
          research: { ...p.research, discounts },
        };
      });
      break;
    }
    case 'anomaly': {
      const science = FINDS.anomaly.science * level;
      gains.science = science;
      give((p) => ({ ...p, planet: { ...p.planet, science: p.planet.science + science } }));
      break;
    }
    case 'asteroid': {
      const cloud = state.system.clouds.find((c) => c.id === cloudId)!;
      const at = asteroidHex(state.system, exploration, cloud);
      if (at) {
        const id = `asteroid-${exploration.asteroids.length + 1}`;
        exploration = {
          ...exploration,
          asteroids: [...exploration.asteroids, { id, hex: at, cloudId }],
        };
        gains.asteroid = id;
      }
      break;
    }
  }
  events.push({ type: 'found', playerId, cloudId, find: card.find, gains });
  return { ...state, players, exploration };
}

/** Offers nobody picked in time take their first card (#105). */
function runFindOffers(state: GameState, events: GameEvent[]): GameState {
  if (!state.findOffers.some((o) => o.expiresAt <= state.tick)) return state;
  const rng = new Rng(state.rng);
  let next = state;
  for (const offer of state.findOffers) {
    if (offer.expiresAt > state.tick) continue;
    next = { ...next, findOffers: next.findOffers.filter((o) => o.id !== offer.id) };
    next = grantFind(
      next,
      offer.playerId,
      offer.cloudId,
      offer.level,
      offer.cards[0]!,
      rng,
      events,
    );
  }
  return { ...next, rng: rng.getState() };
}

/** One tick of movement for every fleet. */
function runFleets(state: GameState, events: GameEvent[]): GameState {
  if (state.fleets.every((f) => f.path.length === 0)) return state;
  const supplied = new Map(state.players.map((p) => [p.id, p.supplied]));
  const fighting = new Set(state.battles);
  // Caravans of players with «Торговые протоколы» fly faster (#27).
  const caravans = new Set(state.contracts.flatMap((c) => (c.caravan ? [c.caravan.fleetId] : [])));
  const caravanSpeed = new Map(state.players.map((p) => [p.id, techMods(p.techs).caravanSpeed]));
  // ...and those between trade partners faster still (#74).
  const partnered = new Set(
    state.contracts.flatMap((c) =>
      c.caravan && hasAgreement(state.agreements, c.seller, c.buyer, 'trade')
        ? [c.caravan.fleetId]
        : [],
    ),
  );
  const enemies = new Map<string, Set<string>>();
  const blockedFor = (owner: string) => {
    let set = enemies.get(owner);
    if (!set) enemies.set(owner, (set = enemyHexes(state, owner)));
    return set;
  };
  return {
    ...state,
    fleets: state.fleets.map((f0) => {
      if (f0.path.length === 0) return f0;
      // A fleet caught in a battle holds its hex unless it evades or retreats (GDD §8);
      // an unarmed one is not held (#158): it cannot fight.
      const armed = isArmed(f0);
      const pinned =
        armed && fighting.has(hexKey(f0.hex)) && !f0.retreating && fleetStance(f0) !== 'evasive';
      if (pinned) return f0;
      let f = f0;
      if (!armed && f.owner !== PIRATES) {
        // Nor does it fly into the enemy's ships (#158): it goes round them, or waits.
        const blocked = blockedFor(f.owner);
        if (f.path.some((h) => blocked.has(hexKey(h)))) {
          const route = findPath(state.system, f.hex, f.path[f.path.length - 1]!, blocked);
          if (!route) return f.progress ? { ...f, progress: 0 } : f;
          // Turned back to the hex it left, if it was on its way into the enemy.
          const same = route[0] && f.path[0] && hexKey(route[0]) === hexKey(f.path[0]);
          f = { ...f, path: route, progress: same ? f.progress : 0 };
          if (!route.length) return f;
        }
      }
      // An aggressive formation stops where its longest gun reaches the enemy (#160, the
      // author's variant A): the long guns fire, the short ones guard them.
      if (holdsAtRange(state, f)) return f.progress ? { ...f, progress: 0 } : f;
      const base = supplied.get(f.owner) ? 1 : FLEET.unsuppliedSpeed;
      const bonus = caravans.has(f.id)
        ? (caravanSpeed.get(f.owner) ?? 0) +
          (partnered.has(f.id) ? TRADE_AGREEMENT.caravanSpeed : 0)
        : 0;
      const moved = moveFleet(f, base * (1 + bonus));
      if (moved.path.length === 0) {
        events.push({ type: 'fleetArrived', playerId: f.owner, fleetId: f.id });
        return f.retreating ? { ...moved, retreating: false } : moved;
      }
      return moved;
    }),
  };
}

/**
 * One combat round everywhere hostile fleets share a hex (GDD §8). Ships
 * that drop to zero hull are destroyed and leave debris; a fleet whose hull
 * falls below its retreat share heads home.
 */
function runCombat(state: GameState, events: GameEvent[]): GameState {
  const byHex = new Map<string, FleetState[]>();
  for (const f of state.fleets) {
    const k = hexKey(f.hex);
    byHex.set(k, [...(byHex.get(k) ?? []), f]);
  }
  const capitals = new Map(state.players.map((p) => [hexKey(capitalHex(state, p.id)), p] as const));
  const gunsOf = (key: string) => {
    const owner = capitals.get(key);
    return owner
      ? owner.planet.buildings.reduce((a, b) => a + BUILDINGS[b.id].guns * b.level, 0)
      : 0;
  };

  // Battles in a hex (GDD §8): there fleets are pinned, as before ranged fire (#117).
  const battles: string[] = [];
  for (const [key, fleets] of byHex) {
    const owner = capitals.get(key);
    const guns = gunsOf(key);
    const sides = [
      ...new Set([...fleets.map((f) => f.owner), ...(owner && guns > 0 ? [owner.id] : [])]),
    ];
    const armed = fleets.filter(isArmed).map((f) => f.owner);
    const hostile = sides.some((a) =>
      sides.some((b) => atWar(state, a, b) && (armed.includes(a) || (owner?.id === a && guns > 0))),
    );
    if (!hostile) continue;
    battles.push(key);
    if (!state.battles.includes(key)) events.push({ type: 'battleStarted', hex: fleets[0]!.hex });
  }

  // Ranged fire (#117): each armed ship reaches enemies within its range; a fleet in
  // a dust cloud is a target only from the same cloud or point-blank.
  const combatants = new Map<number, Combatant[]>();
  const crew = (f: FleetState): Combatant[] => {
    let c = combatants.get(f.id);
    if (!c) {
      c = f.ships.map((s) => ({
        shipId: s.id,
        fleetId: f.id,
        owner: f.owner,
        cls: s.cls,
        hull: shipHull(s),
        stance: fleetStance(f),
        drill: formationDrill(f),
      }));
      combatants.set(f.id, c);
    }
    return c;
  };
  // A formation's convoy (#160): its unarmed ships are shot at only once its warships
  // are gone.
  const targetsIn = (f: FleetState): Combatant[] => {
    const all = crew(f);
    if (!isFormation(f)) return all;
    const guns = all.filter((c) => SHIP_COMBAT[c.cls].damage > 0);
    return guns.length ? guns : all;
  };
  const findsLeft = state.exploration.findsLeft;
  const aims = (a: FleetState, b: FleetState, d: number) => {
    if (d <= 1) return true;
    const cloud = liveCloudAt(state.system, findsLeft, b.hex);
    return !cloud || liveCloudAt(state.system, findsLeft, a.hex)?.id === cloud.id;
  };
  const shooters: { ship: Combatant; reach: Combatant[] }[] = [];
  for (const a of state.fleets) {
    if (!isArmed(a)) continue;
    const range = Math.max(...a.ships.map((s) => SHIP_COMBAT[s.cls].range));
    const enemies = state.fleets
      .filter((b) => b.id !== a.id && atWar(state, a.owner, b.owner))
      .map((b) => ({ b, d: hexDistance(a.hex, b.hex) }))
      .filter(({ b, d }) => d <= range && aims(a, b, d));
    if (!enemies.length) continue;
    for (const ship of crew(a)) {
      const r = SHIP_COMBAT[ship.cls].range;
      const reach = enemies.filter(({ d }) => d <= r).flatMap(({ b }) => targetsIn(b));
      if (reach.length && SHIP_COMBAT[ship.cls].damage > 0) shooters.push({ ship, reach });
    }
  }
  const batteries: { damage: number; reach: Combatant[] }[] = [];
  for (const [key, owner] of capitals) {
    const guns = gunsOf(key);
    const reach = (byHex.get(key) ?? [])
      .filter((f) => atWar(state, owner.id, f.owner))
      .flatMap(targetsIn);
    if (guns > 0 && reach.length) batteries.push({ damage: guns, reach });
  }

  for (const key of state.battles) {
    if (!battles.includes(key)) {
      const h = byHex.get(key)?.[0]?.hex;
      if (h) events.push({ type: 'battleEnded', hex: h });
    }
  }
  if (!shooters.length && !batteries.length && !battles.length && !state.battles.length) {
    return state;
  }

  // A formation's cover (#160): a ship strong against the shooter takes the hit.
  const fleetOf = new Map(state.fleets.map((f) => [f.id, f]));
  const { hulls, shots } = rangedRound(shooters, batteries, (c) => {
    const f = fleetOf.get(c.fleetId);
    return f && isFormation(f) ? targetsIn(f) : [];
  });
  const fleetById = new Map(state.fleets.map((f) => [f.id, f]));
  if (shots.length) {
    events.push({
      type: 'volley',
      shots: shots.map((s) => ({
        from: fleetById.get(s.from)!.hex,
        to: fleetById.get(s.to)!.hex,
        owner: fleetById.get(s.from)!.owner,
        cls: s.cls,
      })),
    });
  }
  const debris = new Map(state.debris.map((d) => [hexKey(d.hex), d]));
  const fleets: FleetState[] = [];
  for (const f of state.fleets) {
    if (!f.ships.some((s) => hulls.has(s.id))) {
      fleets.push(f);
      continue;
    }
    for (const s of f.ships) {
      if ((hulls.get(s.id) ?? 1) > 0) continue;
      events.push({ type: 'shipDestroyed', playerId: f.owner, ship: s.cls, hex: f.hex });
      const key = hexKey(f.hex);
      const metals = SHIP_CLASSES[s.cls].cost.alloys * COMBAT.debrisPerAlloy;
      debris.set(key, { hex: f.hex, metals: (debris.get(key)?.metals ?? 0) + metals });
    }
    const ships = f.ships
      .map((s) => ({ ...s, hull: hulls.get(s.id) ?? shipHull(s) }))
      .filter((s) => s.hull > 0);
    if (!ships.length) {
      events.push({ type: 'fleetDestroyed', playerId: f.owner, fleetId: f.id, lead: leadShip(f) });
      continue;
    }
    let next: FleetState = { ...f, ships };
    // An unarmed fleet does not run (#154, the author's decision): a constructor finishes
    // what it builds, and is recalled by hand.
    if (!f.retreating && f.owner !== PIRATES && isArmed(f) && hullShare(next) < fleetRetreatAt(f)) {
      const path = findPath(state.system, f.hex, capitalHex(state, f.owner));
      if (path && path.length) {
        next = { ...next, path, progress: 0, scan: null, retreating: true };
        events.push({ type: 'fleetRetreating', playerId: f.owner, fleetId: f.id });
      }
    }
    fleets.push(next);
  }
  return { ...state, fleets, battles, debris: [...debris.values()] };
}

/**
 * Pirates each tick (GDD §7): warned bands appear; once a cycle, level-3
 * bands left alone too long start roaming, and idle roamers pick a new
 * target — an outpost if there is one, else a dust cloud.
 */
function runPirates(state: GameState, events: GameEvent[]): GameState {
  let next = state;
  const due = state.pirateWarnings.filter((w) => w.spawnTick <= state.tick);
  if (due.length) {
    let nextId = next.nextId;
    const fleets: FleetState[] = [];
    const bands: PirateBand[] = [];
    for (const w of due) {
      const def = PIRATE_BANDS[w.level];
      if (!def) continue;
      const ships = def.ships.map((cls) => ({ id: nextId++, cls }));
      const fleet: FleetState = {
        id: nextId++,
        owner: PIRATES,
        hex: w.hex,
        ships,
        path: [],
        progress: 0,
        scan: null,
      };
      fleets.push(fleet);
      bands.push({
        fleetId: fleet.id,
        level: w.level,
        cloudId: w.cloudId,
        spawnedTick: state.tick,
        roaming: false,
      });
      events.push({ type: 'piratesSpawned', fleetId: fleet.id, cloudId: w.cloudId });
    }
    next = {
      ...next,
      nextId,
      fleets: [...next.fleets, ...fleets],
      pirates: [...next.pirates, ...bands],
      pirateWarnings: next.pirateWarnings.filter((w) => w.spawnTick > state.tick),
    };
  }
  if (state.tick % BALANCE.cycleTicks !== 0 || next.pirates.length === 0) return next;

  const rng = new Rng(next.rng);
  const targets = [...next.outposts.map((o) => o.hex), ...next.system.clouds.map((c) => c.center)];
  const pirates = next.pirates.map((b) =>
    !b.roaming && b.level >= 3 && state.tick - b.spawnedTick >= PIRATE_RULES.roamAfterTicks
      ? (events.push({ type: 'piratesRoaming', fleetId: b.fleetId, cloudId: b.cloudId }),
        { ...b, roaming: true })
      : b,
  );
  const roamers = new Set(pirates.filter((b) => b.roaming).map((b) => b.fleetId));
  const fleets = next.fleets.map((f) => {
    if (!roamers.has(f.id) || f.path.length > 0 || targets.length === 0) return f;
    const path = findPath(next.system, f.hex, rng.pick(targets));
    return path && path.length ? { ...f, path, progress: 0 } : f;
  });
  return { ...next, pirates, fleets, rng: rng.getState() };
}

/** The longest reach of any ship (#117). */
const MAX_RANGE = Math.max(...Object.values(SHIP_COMBAT).map((c) => c.range));

/** Pirate bands destroyed this round: their loot goes to the players who fought them. */
function settlePirates(before: GameState, after: GameState, events: GameEvent[]): GameState {
  if (before.pirates.length === 0) return after;
  const alive = new Set(after.fleets.map((f) => f.id));
  const beaten = before.pirates.filter((b) => !alive.has(b.fleetId));
  if (!beaten.length) return after;
  let players = after.players;
  for (const band of beaten) {
    const hex = before.fleets.find((f) => f.id === band.fleetId)?.hex;
    if (!hex) continue;
    // Those who could fire on it share the loot (#117: fire reaches up to 3 hexes).
    const winners = [
      ...new Set(
        after.fleets
          .filter((f) => f.owner !== PIRATES && isArmed(f) && hexDistance(f.hex, hex) <= MAX_RANGE)
          .map((f) => f.owner),
      ),
    ].sort();
    const def = PIRATE_BANDS[band.level]!;
    events.push({ type: 'piratesDefeated', fleetId: band.fleetId, winners, loot: def.loot });
    if (!winners.length) continue;
    const share = {
      alloys: def.loot.alloys / winners.length,
      exotics: def.loot.exotics / winners.length,
    };
    players = players.map((p) =>
      winners.includes(p.id)
        ? {
            ...p,
            pirateKills: p.pirateKills + 1,
            prestige: roundMoney(p.prestige + PRESTIGE.pirateBand),
            goods: {
              ...p.goods,
              alloys: p.goods.alloys + share.alloys,
              exotics: p.goods.exotics + share.exotics,
            },
          }
        : p,
    );
  }
  const gone = new Set(beaten.map((b) => b.fleetId));
  return { ...after, players, pirates: after.pirates.filter((b) => !gone.has(b.fleetId)) };
}

/**
 * Diplomacy each tick (GDD §8): declared wars start after their delay,
 * raids and sieges progress while the attacker holds the target, and fail
 * as soon as defenders show up or the attacker leaves.
 */
function runDiplomacy(state: GameState, events: GameEvent[]): GameState {
  let next = state;
  const due = next.pendingWars.filter((w) => w.startsAt <= next.tick);
  if (due.length) {
    for (const w of due) events.push({ type: 'warStarted', a: w.by, b: w.target });
    next = {
      ...next,
      wars: [...next.wars, ...due.map((w) => ({ a: w.by, b: w.target, since: next.tick }))],
      pendingWars: next.pendingWars.filter((w) => w.startsAt > next.tick),
    };
  }
  if (!next.raids.length && !next.sieges.length) return next;

  const armedHere = (owner: string, key: string) =>
    next.fleets.some((f) => f.owner === owner && hexKey(f.hex) === key && isArmed(f));
  const raids: Raid[] = [];
  for (const raid of next.raids) {
    const fleet = next.fleets.find((f) => f.id === raid.fleetId);
    const target =
      raid.target.kind === 'planet'
        ? capitalHex(next, raid.victim)
        : next.outposts.find((o) => raid.target.kind === 'outpost' && o.id === raid.target.id)?.hex;
    const key = target && hexKey(target);
    const holding =
      fleet &&
      key &&
      fleet.path.length === 0 &&
      hexKey(fleet.hex) === key &&
      !armedHere(raid.victim, key);
    if (!holding) {
      events.push({
        type: 'raidFailed',
        playerId: raid.owner,
        victim: raid.victim,
        fleetId: raid.fleetId,
      });
      continue;
    }
    if (raid.progress + 1 < RAID.holdTicks) {
      raids.push({ ...raid, progress: raid.progress + 1 });
      continue;
    }
    next = plunder(next, raid, fleet, events);
  }
  next = { ...next, raids };

  const sieges: Siege[] = [];
  for (const siege of next.sieges) {
    const key = hexKey(capitalHex(next, siege.victim));
    if (!armedHere(siege.owner, key) || armedHere(siege.victim, key)) {
      events.push({ type: 'siegeBroken', playerId: siege.owner, victim: siege.victim });
      continue;
    }
    if (siege.progress + 1 < INVASION.siegeTicks) {
      sieges.push({ ...siege, progress: siege.progress + 1 });
      continue;
    }
    events.push({ type: 'vassalized', vassal: siege.victim, overlord: siege.owner });
    next = {
      ...next,
      wars: next.wars.filter((w) => !pairIs(w, siege.owner, siege.victim)),
      raids: next.raids.filter(
        (r) => !pairIs({ a: r.owner, b: r.victim }, siege.owner, siege.victim),
      ),
      players: next.players.map((p) =>
        p.id === siege.victim ? { ...p, vassalOf: siege.owner } : p,
      ),
    };
  }
  return { ...next, sieges };
}

/** What a raiding fleet can carry off: holds + a little per warship, +50% for the Reforged. */
function raidCapacity(raider: PlayerState, fleet: FleetState): number {
  const armed = fleet.ships.filter((s) => SHIP_COMBAT[s.cls].damage > 0).length;
  const holds = fleet.ships.reduce((a, s) => a + SHIP_CLASSES[s.cls].cargo, 0);
  return (
    (holds + armed * RAID.cargoPerArmedShip) *
    (raider.faction === 'reforged' ? RAID.reforgedLoot : 1)
  );
}

/** Strips a caravan's cargo, most valuable goods first, up to `capacity`. */
/** What a fleet can carry for a contract: its holds, more on a level-2 trade route (#74). */
export function caravanCargo(state: GameState, contract: Contract, fleet: FleetState): number {
  const holds = fleet.ships.reduce((a, s) => a + SHIP_CLASSES[s.cls].cargo, 0);
  const level = routeLevel(state.agreements, state.tradeRoutes, contract.seller, contract.buyer);
  return level >= 2 ? Math.floor(holds * (1 + TRADE_AGREEMENT.cargoBonus)) : holds;
}

/** One more delivery on a trade route; announces a new route level. */
function countDelivery(state: GameState, a: string, b: string, events: GameEvent[]): GameState {
  const before = routeLevel(state.agreements, state.tradeRoutes, a, b);
  const route = state.tradeRoutes.find((r) => pairIs(r, a, b));
  const tradeRoutes = route
    ? state.tradeRoutes.map((r) => (r === route ? { ...r, deliveries: r.deliveries + 1 } : r))
    : [...state.tradeRoutes, { a, b, deliveries: 1 }];
  const next = { ...state, tradeRoutes };
  const after = routeLevel(next.agreements, tradeRoutes, a, b);
  if (after > before) events.push({ type: 'routeLevelUp', a, b, level: after });
  return next;
}

function robCaravan(
  state: GameState,
  contract: Contract,
  by: string,
  capacity: number,
  events: GameEvent[],
): GameState {
  const caravan = contract.caravan!;
  const cargo = { ...caravan.cargo };
  const loot: Partial<Record<TradeGoodId, number>> = {};
  // A level-3 trade route armours its caravans: robbers get only part of the cargo (#74).
  const armoured =
    routeLevel(state.agreements, state.tradeRoutes, contract.seller, contract.buyer) >= 3;
  let left = armoured
    ? Math.min(capacity, cargoTotal(cargo) * TRADE_AGREEMENT.armoredLootShare)
    : capacity;
  const goods = (Object.keys(cargo) as TradeGoodId[]).sort(
    (a, b) => TRADE_GOODS[b].basePrice - TRADE_GOODS[a].basePrice,
  );
  for (const g of goods) {
    const take = Math.min(cargo[g] ?? 0, left);
    if (take <= 0) continue;
    loot[g] = take;
    cargo[g] = (cargo[g] ?? 0) - take;
    left -= take;
  }
  events.push({ type: 'caravanRobbed', contractId: contract.id, by, loot });
  return {
    ...state,
    contracts: state.contracts.map((c) =>
      c.id === contract.id ? { ...c, caravan: { ...caravan, cargo } } : c,
    ),
    players: state.players.map((p) => {
      if (p.id !== by) return p;
      const g = { ...p.goods };
      for (const [k, n] of Object.entries(loot) as [TradeGoodId, number][]) g[k] += n;
      return { ...p, goods: g };
    }),
  };
}

/** Ends a signed contract as broken by `by`, who loses reputation; cargo on board goes home. */
function breakContract(
  state: GameState,
  contract: Contract,
  by: string,
  events: GameEvent[],
): GameState {
  events.push({ type: 'contractBroken', contractId: contract.id, by });
  const cargo = contract.caravan?.cargo ?? {};
  return {
    ...state,
    contracts: state.contracts.filter((c) => c.id !== contract.id),
    players: state.players.map((p) => {
      let next = p;
      if (p.id === by)
        next = { ...next, reputation: Math.max(0, p.reputation - TRADE.breachPenalty) };
      if (p.id === contract.seller && cargoTotal(cargo) > 0) {
        const g = { ...next.goods };
        for (const [k, n] of Object.entries(cargo) as [TradeGoodId, number][]) g[k] += n;
        next = { ...next, goods: g };
      }
      return next;
    }),
  };
}

/**
 * Every caravan each tick (GDD §6): load at the seller once a cycle, fly,
 * deliver and collect at the buyer, fly back with any barter goods.
 * Pirates in the same hex strip a loaded caravan.
 */
function runCaravans(state: GameState, events: GameEvent[]): GameState {
  if (!state.contracts.some((c) => c.caravan)) return state;
  let next = payTariffs(state, events);
  for (const original of state.contracts) {
    const c = next.contracts.find((x) => x.id === original.id);
    if (!c?.caravan) continue;
    const fleet = next.fleets.find((f) => f.id === c.caravan!.fleetId);
    if (!fleet) {
      next = {
        ...next,
        contracts: next.contracts.map((x) => (x.id === c.id ? { ...x, caravan: null } : x)),
      };
      continue;
    }
    if (cargoTotal(c.caravan.cargo) > 0) {
      const pirates = next.fleets.some(
        (f) => f.owner === PIRATES && hexKey(f.hex) === hexKey(fleet.hex),
      );
      if (pirates) {
        next = robCaravan(next, c, PIRATES, Infinity, events);
        continue;
      }
    }
    if (fleet.path.length > 0) continue;
    const seller = next.players.find((p) => p.id === c.seller)!;
    const buyer = next.players.find((p) => p.id === c.buyer)!;
    const at = (id: string) => hexKey(fleet.hex) === hexKey(capitalHex(next, id));
    const go = (to: string): Hex[] => findPath(next.system, fleet.hex, capitalHex(next, to)) ?? [];
    const update = (contract: Partial<Contract>, path?: Hex[]) => {
      next = {
        ...next,
        contracts: next.contracts.map((x) => (x.id === c.id ? { ...x, ...contract } : x)),
        fleets: path
          ? next.fleets.map((f) => (f.id === fleet.id ? { ...f, path, progress: 0 } : f))
          : next.fleets,
      };
    };

    if (c.caravan.phase === 'atSeller') {
      if (!at(c.seller)) {
        update({}, go(c.seller));
        continue;
      }
      if (next.tick % BALANCE.cycleTicks !== 0) continue;
      // Sanctions and embargoes freeze loading; the seller is not to blame for it.
      if (contractBlock(next.levers, c.seller, c.buyer)) continue;
      if (seller.goods[c.good] < c.quantity) {
        const missed = c.missed + 1;
        if (missed >= TRADE.missedToBreak) next = breakContract(next, c, c.seller, events);
        else update({ missed });
        continue;
      }
      next = {
        ...next,
        players: next.players.map((p) =>
          p.id === c.seller
            ? { ...p, goods: { ...p.goods, [c.good]: p.goods[c.good] - c.quantity } }
            : p,
        ),
      };
      update(
        {
          missed: 0,
          caravan: { ...c.caravan, phase: 'toBuyer', cargo: { [c.good]: c.quantity }, tolled: [] },
        },
        go(c.buyer),
      );
      continue;
    }

    if (c.caravan.phase === 'toBuyer') {
      if (!at(c.buyer)) {
        update({}, go(c.buyer));
        continue;
      }
      const carried = c.caravan.cargo[c.good] ?? 0;
      const value = (c.price ?? next.market[c.good].price) * carried;
      const cost = c.price !== null ? value / buyer.currency.rate : 0;
      const canPay =
        c.price !== null ? buyer.cash >= cost : buyer.goods[c.barter!.good] >= c.barter!.quantity;
      if (!canPay) {
        next = breakContract(next, c, c.buyer, events);
        continue;
      }
      const partners = hasAgreement(next.agreements, c.seller, c.buyer, 'trade');
      const income = value * TRADE.tradeIncome * (partners ? TRADE_AGREEMENT.incomeFactor : 1);
      const back: Partial<Record<TradeGoodId, number>> = c.barter
        ? { [c.barter.good]: c.barter.quantity }
        : {};
      next = {
        ...next,
        players: next.players.map((p) => {
          if (p.id === c.buyer) {
            const goods = { ...p.goods, [c.good]: p.goods[c.good] + carried };
            if (c.barter) goods[c.barter.good] -= c.barter.quantity;
            return {
              ...p,
              goods,
              cash: roundMoney(
                p.cash - cost + (income * TRADE.importerIncomeFactor) / p.currency.rate,
              ),
              currency: { ...p.currency, cycleTurnover: p.currency.cycleTurnover + value },
            };
          }
          if (p.id === c.seller) {
            const paid = c.price !== null ? value : 0;
            return {
              ...p,
              cycleExports: p.cycleExports + value,
              cash: roundMoney(
                p.cash + (paid + income * TRADE.exporterIncomeFactor) / p.currency.rate,
              ),
              currency: { ...p.currency, cycleTurnover: p.currency.cycleTurnover + value },
            };
          }
          return p;
        }),
      };
      events.push({ type: 'delivered', contractId: c.id, gs: roundMoney(value) });
      if (partners) next = countDelivery(next, c.seller, c.buyer, events);
      update(
        {
          deliveriesLeft: c.deliveriesLeft - 1,
          caravan: { ...c.caravan, phase: 'toSeller', cargo: back, tolled: [] },
        },
        go(c.seller),
      );
      continue;
    }

    // toSeller: unload any barter goods; the next delivery or the end.
    if (!at(c.seller)) {
      update({}, go(c.seller));
      continue;
    }
    const cargo = c.caravan.cargo;
    next = {
      ...next,
      players: next.players.map((p) => {
        if (p.id !== c.seller || cargoTotal(cargo) <= 0) return p;
        const goods = { ...p.goods };
        for (const [k, n] of Object.entries(cargo) as [TradeGoodId, number][]) goods[k] += n;
        return { ...p, goods };
      }),
    };
    if (c.deliveriesLeft <= 0) {
      events.push({
        type: 'contractCompleted',
        contractId: c.id,
        seller: c.seller,
        buyer: c.buyer,
      });
      next = { ...next, contracts: next.contracts.filter((x) => x.id !== c.id) };
    } else {
      update({ caravan: { ...c.caravan, phase: 'atSeller', cargo: {}, tolled: [] } });
    }
  }
  return next;
}

/**
 * Tariffs (GDD §6): a loaded caravan entering the zone of a player with a
 * tariff pays a share of its cargo's value, once per owner and leg.
 */
function payTariffs(state: GameState, events: GameEvent[]): GameState {
  const owners = tariffOwners(state.levers);
  if (owners.size === 0) return state;
  let next = state;
  for (const c of state.contracts) {
    if (!c.caravan || cargoTotal(c.caravan.cargo) <= 0) continue;
    const fleet = next.fleets.find((f) => f.id === c.caravan!.fleetId);
    if (!fleet) continue;
    const tolled = c.caravan.tolled ?? [];
    const due = [
      ...new Set(
        next.outposts
          .filter(
            (o) =>
              owners.has(o.owner) &&
              o.owner !== fleet.owner &&
              // Trade partners do not toll each other's caravans (#74).
              !hasAgreement(next.agreements, o.owner, fleet.owner, 'trade') &&
              !tolled.includes(o.owner) &&
              inZone(o, fleet.hex),
          )
          .map((o) => o.owner),
      ),
    ];
    if (due.length === 0) continue;
    const value = (Object.entries(c.caravan.cargo) as [TradeGoodId, number][]).reduce(
      (sum, [g, n]) => sum + n * next.market[g].price,
      0,
    );
    for (const to of due) {
      const payer = next.players.find((p) => p.id === fleet.owner)!;
      const local = Math.min(payer.cash, (value * LEVER_EFFECTS.tariffShare) / payer.currency.rate);
      const gs = local * payer.currency.rate;
      next = {
        ...next,
        players: next.players.map((p) =>
          p.id === payer.id
            ? { ...p, cash: roundMoney(p.cash - local) }
            : p.id === to
              ? { ...p, cash: roundMoney(p.cash + gs / p.currency.rate) }
              : p,
        ),
      };
      events.push({
        type: 'tariffPaid',
        contractId: c.id,
        payer: payer.id,
        to,
        gs: roundMoney(gs),
      });
    }
    next = {
      ...next,
      contracts: next.contracts.map((x) =>
        x.id === c.id && x.caravan
          ? { ...x, caravan: { ...x.caravan, tolled: [...tolled, ...due] } }
          : x,
      ),
    };
  }
  return next;
}

/** A finished raid: loot within the fleet's capacity, then the fleet heads home. */
function plunder(state: GameState, raid: Raid, fleet: FleetState, events: GameEvent[]): GameState {
  const raider = state.players.find((p) => p.id === raid.owner)!;
  const bonus = raider.faction === 'reforged' ? RAID.reforgedLoot : 1;
  let capacity = raidCapacity(raider, fleet);
  const loot: Partial<Record<TradeGoodId, number>> = {};
  let outposts = state.outposts;
  let players = state.players;

  if (raid.target.kind === 'planet') {
    const victim = state.players.find((p) => p.id === raid.victim)!;
    const byValue = [...TRADE_GOOD_IDS].sort(
      (a, b) => TRADE_GOODS[b].basePrice - TRADE_GOODS[a].basePrice,
    );
    for (const g of byValue) {
      const take = Math.min(victim.goods[g] * RAID.stockShare * bonus, capacity);
      if (take <= 0) continue;
      loot[g] = take;
      capacity -= take;
    }
  } else {
    const target = raid.target;
    const outpost = state.outposts.find((o) => o.id === target.id)!;
    loot[outpost.good] = Math.min(OUTPOSTS[outpost.kind].output * RAID.outpostLootCycles, capacity);
    outposts = outposts.map((o) =>
      o.id === outpost.id
        ? { ...o, downUntil: state.tick + RAID.outpostDownCycles * BALANCE.cycleTicks }
        : o,
    );
  }
  players = players.map((p) => {
    const sign =
      p.id === raid.owner ? 1 : p.id === raid.victim && raid.target.kind === 'planet' ? -1 : 0;
    if (!sign) return p;
    const goods = { ...p.goods };
    for (const [g, n] of Object.entries(loot) as [TradeGoodId, number][]) {
      goods[g] = Math.max(0, goods[g] + sign * n);
    }
    return { ...p, goods };
  });
  events.push({ type: 'raided', playerId: raid.owner, victim: raid.victim, loot });
  const home = findPath(state.system, fleet.hex, capitalHex(state, raid.owner));
  return {
    ...state,
    players,
    outposts,
    fleets: state.fleets.map((f) =>
      f.id === fleet.id && home && home.length ? { ...f, path: home, progress: 0 } : f,
    ),
  };
}

/** Vassals pay a quarter of their taxes to their overlord every cycle (GDD §8). */
function runTribute(state: GameState, events: GameEvent[]): GameState {
  const vassals = state.players.filter((p) => p.vassalOf !== null);
  if (!vassals.length) return state;
  const paid = new Map<string, number>();
  const received = new Map<string, number>();
  for (const v of vassals) {
    const local = Math.min(v.cash, (v.planet.lastCycle?.taxes ?? 0) * INVASION.tribute);
    if (local <= 0) continue;
    const gs = local * v.currency.rate;
    paid.set(v.id, local);
    received.set(v.vassalOf!, (received.get(v.vassalOf!) ?? 0) + gs);
    events.push({ type: 'tributePaid', vassal: v.id, overlord: v.vassalOf!, gs: roundMoney(gs) });
  }
  return {
    ...state,
    players: state.players.map((p) => {
      const out = paid.get(p.id) ?? 0;
      const into = received.get(p.id) ?? 0;
      if (!out && !into) return p;
      return { ...p, cash: roundMoney(p.cash - out + (into ? into / p.currency.rate : 0)) };
    }),
  };
}

/**
 * Fleet housekeeping each cycle: ships at their own capital repair, and
 * cargo ships idling on debris salvage it into metals (GDD §8).
 */
function runFleetCycle(state: GameState): GameState {
  const capitals = new Map(state.players.map((p) => [p.id, hexKey(capitalHex(state, p.id))]));
  let debris = state.debris.map((d) => ({ ...d }));
  const salvaged = new Map<string, number>();
  const fleets = state.fleets.map((f) => {
    const key = hexKey(f.hex);
    if (f.path.length === 0) {
      const field = debris.find((d) => hexKey(d.hex) === key && d.metals > 0);
      const cargo = f.ships.reduce((a, s) => a + SHIP_CLASSES[s.cls].cargo, 0);
      if (field && cargo > 0 && f.owner !== PIRATES) {
        const take = Math.min(cargo, field.metals);
        field.metals -= take;
        salvaged.set(f.owner, (salvaged.get(f.owner) ?? 0) + take);
      }
    }
    if (capitals.get(f.owner) !== key) return f;
    if (f.ships.every((s) => s.hull === undefined || s.hull >= SHIP_COMBAT[s.cls].hull)) return f;
    return {
      ...f,
      ships: f.ships.map((s) => {
        const max = SHIP_COMBAT[s.cls].hull;
        return { ...s, hull: Math.min(max, shipHull(s) + max * COMBAT.repairPerCycle) };
      }),
    };
  });
  debris = debris.filter((d) => d.metals > 0);
  return {
    ...state,
    fleets,
    debris,
    players: state.players.map((p) =>
      salvaged.has(p.id)
        ? { ...p, goods: { ...p.goods, metals: p.goods.metals + salvaged.get(p.id)! } }
        : p,
    ),
  };
}

/** What a working outpost gives its owner every cycle: the faction's and the techs' factors applied. */
export function outpostYield(p: PlayerState, o: Pick<OutpostState, 'kind' | 'good'>): number {
  const mods = techMods(p.techs);
  const econ = FACTION_ECONOMY[p.faction];
  return (
    OUTPOSTS[o.kind].output *
    econ.productionFactor *
    (o.good === FACTIONS[p.faction].cheapGood ? econ.cheapGoodFactor : 1) *
    (1 + mods.outpostOutput) *
    (o.good === 'exotics' ? mods.exoticsYield : 1)
  );
}

/**
 * One player's share of a cycle: the planet runs, outposts deliver, the fleet
 * and the outposts are paid for. The currency's rate is left to `runCycle`.
 */
function playerCycle(
  state: GameState,
  p: PlayerState,
  tick: number,
  market: MarketState,
  events: GameEvent[],
): PlayerCycle {
  const mods = techMods(p.techs);
  const raw = fleetUpkeep(shipsOf(state.fleets, p.id), fleetLimit(p.planet) + mods.fleetLimit);
  const upkeep = {
    cash: raw.cash * mods.fleetUpkeep,
    alloys: raw.alloys * mods.fleetUpkeep,
    energy: raw.energy * mods.fleetUpkeep,
  };
  const r = runPlanetCycle({
    planet: p.planet,
    faction: p.faction,
    cash: p.cash,
    goods: p.goods,
    tick,
    extraEnergyDemand: upkeep.energy,
    mods,
    energyFactor: p.blackout && p.blackout.until > tick ? p.blackout.energy : 1,
  });
  for (const index of r.completed) {
    const b = r.planet.buildings[index]!;
    events.push({
      type: 'constructionCompleted',
      playerId: p.id,
      index,
      building: b.id,
      level: b.level,
    });
  }
  if (r.starved) {
    events.push({ type: 'starvation', playerId: p.id, population: r.planet.population });
  }
  // Outposts (GDD §5): a steady output to the owner's stock, upkeep with the fleet's.
  const owned = state.outposts.filter((o) => o.owner === p.id);
  const goods = { ...r.goods };
  const fromOutposts: Partial<Record<TradeGoodId, number>> = {};
  let outpostValue = 0;
  for (const o of owned) {
    if (o.readyTick > tick || (o.downUntil ?? 0) > tick) {
      continue;
    }
    if (o.readyTick > tick - BALANCE.cycleTicks) {
      events.push({ type: 'outpostReady', playerId: p.id, outpostId: o.id, kind: o.kind });
    }
    const made = outpostYield(p, o);
    goods[o.good] += made;
    fromOutposts[o.good] = (fromOutposts[o.good] ?? 0) + made;
    outpostValue += made * market[o.good].price;
  }
  const outputValue =
    TRADE_GOOD_IDS.reduce((sum, g) => sum + r.planet.lastCycle!.produced[g] * market[g].price, 0) +
    outpostValue;
  // Fleet upkeep (GDD §8): whatever cannot be paid leaves the fleets unsupplied.
  const fleetCash = toLocal(upkeep.cash, p.currency.rate);
  const outpostCash = toLocal(outpostUpkeep(owned), p.currency.rate);
  const cashDue = toLocal(upkeep.cash + outpostUpkeep(owned), p.currency.rate);
  const supplied = r.cash >= cashDue && goods.alloys >= upkeep.alloys;
  if (!supplied) events.push({ type: 'upkeepUnpaid', playerId: p.id });
  return {
    player: {
      ...p,
      cash: roundMoney(Math.max(0, r.cash - cashDue)),
      goods: { ...goods, alloys: Math.max(0, goods.alloys - upkeep.alloys) },
      planet: r.planet,
      currency: recordCycle(p.currency, outputValue),
      supplied,
    },
    fromOutposts,
    upkeep: { fleetCash, outpostCash, alloys: upkeep.alloys, energy: upkeep.energy },
    starved: r.starved,
  };
}

interface PlayerCycle {
  readonly player: PlayerState;
  /** Goods the outposts delivered this cycle. */
  readonly fromOutposts: Partial<Record<TradeGoodId, number>>;
  /** What the fleet and the outposts cost, in the player's own currency and goods. */
  readonly upkeep: {
    readonly fleetCash: number;
    readonly outpostCash: number;
    readonly alloys: number;
    readonly energy: number;
  };
  readonly starved: boolean;
}

/**
 * What the next cycle will do to a player's stock, treasury and science if
 * nothing changes before it (#101): the planet's output and needs, the
 * outposts, the upkeep of the fleet and the outposts. Purchases, trade and
 * construction are not in it — they are the player's choice, not a cycle's.
 * The cycle itself runs on a copy, so the forecast is exactly what `step`
 * would do now.
 */
export interface CycleForecast {
  /** Ticks until the cycle. */
  readonly inTicks: number;
  readonly cash: {
    readonly taxes: number;
    readonly fleet: number;
    readonly outposts: number;
    readonly net: number;
  };
  readonly goods: Readonly<
    Record<
      TradeGoodId,
      {
        readonly made: number;
        readonly used: number;
        readonly outposts: number;
        readonly fleet: number;
        readonly net: number;
      }
    >
  >;
  readonly science: number;
  /** The fleet's energy, burned with the planet's (helium-3 in the reactors). */
  readonly fleetEnergy: number;
  /** False when the fleet and outposts will go unpaid: unsupplied fleets crawl (FLEET.unsuppliedSpeed). */
  readonly supplied: boolean;
  readonly starving: boolean;
}

export function cycleForecast(state: GameState, playerId: string): CycleForecast | null {
  const p = state.players.find((x) => x.id === playerId);
  if (!p) return null;
  const inTicks = BALANCE.cycleTicks - (state.tick % BALANCE.cycleTicks);
  const c = playerCycle(state, p, state.tick + inTicks, state.market, []);
  const report = c.player.planet.lastCycle!;
  const goods = Object.fromEntries(
    TRADE_GOOD_IDS.map((g) => {
      const outposts = c.fromOutposts[g] ?? 0;
      const fleet = g === 'alloys' ? c.upkeep.alloys : 0;
      return [
        g,
        {
          made: report.produced[g],
          used: report.consumed[g],
          outposts,
          fleet,
          net: report.produced[g] - report.consumed[g] + outposts - fleet,
        },
      ];
    }),
  ) as CycleForecast['goods'];
  return {
    inTicks,
    cash: {
      taxes: report.taxes,
      fleet: c.upkeep.fleetCash,
      outposts: c.upkeep.outpostCash,
      net: report.taxes - c.upkeep.fleetCash - c.upkeep.outpostCash,
    },
    goods,
    science: report.science,
    fleetEnergy: c.upkeep.energy,
    supplied: c.player.supplied,
    starving: report.fed.food < 1 - 1e-9,
  };
}

/**
 * «Торговые алгоритмы» once a cycle (#123): every rule becomes an ordinary
 * exchange deal at the exchange's prices and fees. A sale needs the good to
 * be made regularly — the last cycle's net output, outposts included, above
 * zero — and a price not in a slump; a purchase, a price not inflated and
 * the money. A deal the exchange would refuse is skipped without a note.
 */
/**
 * What a trading rule keeps in stock (#131, #143): what the next cycle takes from it.
 * The population eats before the producers run, and producers take their inputs from
 * the stock; a rule sells above this and buys up to it (plus an optional keep, 0 now).
 */
export function autoTradeReserve(player: PlayerState, good: TradeGoodId): number {
  const needs = populationNeeds(player.planet, player.faction);
  return Math.max(
    player.planet.lastCycle?.consumed[good] ?? 0,
    good === 'food' || good === 'electronics' ? needs[good] : 0,
  );
}

function runAutoTrade(state: GameState, events: GameEvent[]): GameState {
  let next = state;
  for (const p of state.players) {
    if (!p.autoTrade || !techMods(p.techs).autoTrade) continue;
    for (const [good, rule] of Object.entries(p.autoTrade) as [TradeGoodId, AutoTradeRule][]) {
      const me = next.players.find((x) => x.id === p.id)!;
      const ratio = next.market[good].price / TRADE_GOODS[good].basePrice;
      let command: Command | null = null;
      const report = me.planet.lastCycle;
      const reserve = autoTradeReserve(me, good);
      if (rule.mode === 'sell') {
        const fromOutposts = next.outposts
          .filter((o) => o.owner === me.id && o.good === good && o.readyTick <= next.tick)
          .reduce((sum, o) => sum + outpostYield(me, o), 0);
        const made = report ? report.produced[good] - report.consumed[good] + fromOutposts : 0;
        const spare = Math.floor(me.goods[good] - rule.keep - reserve);
        if (made > 0 && spare >= 1 && ratio >= AUTO_TRADE.minSellRatio) {
          command = {
            type: 'marketSell',
            playerId: me.id,
            good,
            quantity: Math.min(spare, MARKET.maxOrderUnits),
          };
        }
      } else {
        const missing = Math.ceil(rule.keep + reserve - me.goods[good]);
        if (missing >= 1 && ratio <= AUTO_TRADE.maxBuyRatio) {
          command = {
            type: 'marketBuy',
            playerId: me.id,
            good,
            quantity: Math.min(missing, MARKET.maxOrderUnits),
          };
        }
      }
      if (command && !commandBlocker(next, command)) next = applyCommand(next, command, events);
    }
  }
  return next;
}

/**
 * One economic cycle (GDD §2: every 30 s): every planet runs, then every
 * currency's rate is recalculated from the new output and turnover.
 */
function runCycle(
  state: GameState,
  tick: number,
  market: MarketState,
  events: GameEvent[],
): GameState {
  const afterPlanets = state.players.map((p) => playerCycle(state, p, tick, market, events).player);
  const shares = tradeShares(afterPlanets.map((p) => p.currency));
  const players = afterPlanets.map((p, i) => ({
    ...p,
    currency: {
      ...p.currency,
      rate: computeRate(p.currency, p.planet.population, shares[i]!, afterPlanets.length),
    },
    // Influence (GDD §6): trade share, exports and the reserve status, every cycle.
    influence: Math.min(
      INFLUENCE.max,
      p.influence + influenceGain(shares[i]!, p.cycleExports, state.reserve.issuer === p.id),
    ),
    cycleExports: 0,
  }));

  const { reserve, change } = updateReserve(
    state.reserve,
    players.map((p, i) => ({
      id: p.id,
      share: shares[i]!,
      output: windowOutput(p.currency),
      commerceLevel: Math.max(
        0,
        ...p.planet.buildings.filter((b) => b.id === 'commerceCenter').map((b) => b.level),
      ),
    })),
  );
  if (change?.type === 'declared')
    events.push({ type: 'reserveDeclared', playerId: change.playerId });
  return {
    ...state,
    reserve,
    players: players.map((p) => {
      if (change?.type !== 'lost' || p.id !== change.playerId) return p;
      // Losing the status sets off a currency crisis (GDD §6).
      const currency = startCrisis(p.currency);
      events.push({ type: 'reserveLost', playerId: p.id, rate: currency.rate });
      return { ...p, currency };
    }),
  };
}

/** Levers whose time is up; sanctions that never found a second backer are refunded. */
function endLevers(state: GameState, tick: number, events: GameEvent[]): GameState {
  let players = state.players;
  for (const l of state.levers) {
    if (l.until > tick) continue;
    const lapsed = l.status === 'gathering';
    events.push({ type: 'leverEnded', leverId: l.id, lever: l.kind, lapsed });
    if (lapsed) {
      players = players.map((p) =>
        l.by.includes(p.id)
          ? { ...p, influence: Math.min(INFLUENCE.max, p.influence + LEVERS[l.kind].cost) }
          : p,
      );
    }
  }
  return { ...state, players, levers: state.levers.filter((l) => l.until > tick) };
}

/**
 * Exchange events at the close of a cycle (GDD §6): the strongest net trade
 * pressure past the threshold announces a glut or a shortage; if it still
 * holds a cycle later, the price drops or jumps. Then the exchange stays
 * quiet for a while, whichever way it went.
 */
function runMarketEvents(state: GameState, tick: number, events: GameEvent[]): GameState {
  const pressure = (good: TradeGoodId) => windowNet(state.market[good]) * TRADE_GOODS[good].impact;
  const kindOf = (good: TradeGoodId) => {
    const p = pressure(good);
    if (p > MARKET_EVENTS.thresholdPressure) return 'glut';
    return p < -MARKET_EVENTS.thresholdPressure ? 'shortage' : null;
  };
  const quiet = tick + MARKET_EVENTS.cooldownCycles * BALANCE.cycleTicks;
  const warned = state.marketWarnings[0];
  if (warned) {
    const { good, kind } = warned;
    if (kindOf(good) !== kind) {
      events.push({ type: 'marketEvent', good, kind, phase: 'averted' });
      return { ...state, marketWarnings: [], marketQuietUntil: quiet };
    }
    const factor =
      kind === 'glut' ? MARKET_EVENTS.glutPriceFactor : MARKET_EVENTS.shortagePriceFactor;
    const shocked = shockPrice(state.market, good, factor);
    events.push({ type: 'marketEvent', good, kind, phase: 'struck' });
    return {
      ...state,
      // The shock settles it: the count starts over.
      market: { ...shocked, [good]: { ...shocked[good], netHistory: [] } },
      marketWarnings: [],
      marketQuietUntil: quiet,
    };
  }
  if (tick < state.marketQuietUntil) return state;
  let strongest: TradeGoodId | null = null;
  for (const good of TRADE_GOOD_IDS) {
    if (!kindOf(good)) continue;
    if (strongest === null || Math.abs(pressure(good)) > Math.abs(pressure(strongest))) {
      strongest = good;
    }
  }
  if (strongest === null) return state;
  const kind = kindOf(strongest)!;
  events.push({ type: 'marketEvent', good: strongest, kind, phase: 'warning' });
  return { ...state, marketWarnings: [{ good: strongest, kind }] };
}

/** Backers sanctions need: a coalition, but never more than the rivals a target has. */
const sanctionsBackers = (state: GameState): number =>
  Math.min(LEVER_EFFECTS.sanctionsBackers, state.players.length - 1);

/** The issuer's cut of foreign exchange deals; «Межзвёздные финансы» raise it (#27). */
function seigniorageOf(state: GameState, issuer: string): number {
  const p = state.players.find((x) => x.id === issuer);
  return p ? techMods(p.techs).seigniorage : RESERVE.seigniorage;
}

/**
 * Research at the close of a cycle (GDD §10): the science stock flows into
 * the technologies under way, shared equally; a technology whose price is
 * met is learnt. Prices fall as other players learn the same technology.
 */
function runResearch(state: GameState, events: GameEvent[]): GameState {
  if (state.players.every((p) => p.research.active.length === 0)) return state;
  const knowers = (tech: TechId) => state.players.filter((p) => p.techs.includes(tech)).length;
  return {
    ...state,
    players: state.players.map((p) => {
      const r = p.research;
      if (r.active.length === 0) return p;
      const needs = r.active.map((t) =>
        Math.max(0, techPrice(t, knowers(t), r.discounts[t] ?? 0) - (r.progress[t] ?? 0)),
      );
      const given = shareScience(p.planet.science, needs);
      const progress = { ...r.progress };
      const done: TechId[] = [];
      let spent = 0;
      r.active.forEach((t, i) => {
        spent += given[i]!;
        progress[t] = Math.round(((progress[t] ?? 0) + given[i]!) * 1e6) / 1e6;
        if (given[i]! >= needs[i]! - 1e-9) done.push(t);
      });
      let prestige = p.prestige;
      for (const t of done) {
        delete progress[t];
        prestige += PRESTIGE.techByTier[TECHS[t].tier];
        events.push({ type: 'researchCompleted', playerId: p.id, tech: t });
      }
      return {
        ...p,
        prestige: roundMoney(prestige),
        techs: [...p.techs, ...done],
        planet: {
          ...p.planet,
          science: Math.max(0, Math.round((p.planet.science - spent) * 1e6) / 1e6),
        },
        research: { ...r, active: r.active.filter((t) => !done.includes(t)), progress },
      };
    }),
  };
}

/**
 * The crisis director at the close of a cycle (GDD §9): the index grows from
 * time and from what players do; a crossed threshold plans a regular crisis
 * (one at a time, none too close to the final); the final crisis is planned
 * when the index reaches the top or its hour comes.
 */
function runDirector(state: GameState, tick: number, events: GameEvent[]): GameState {
  const producing = state.outposts.filter((o) => o.readyTick <= tick && (o.downUntil ?? 0) <= tick);
  const gain = instabilityGain({
    debrisMetals: state.debris.reduce((sum, d) => sum + d.metals, 0),
    outposts: producing,
    system: state.system,
    coronaStages: state.megastructures.reduce((sum, m) => sum + m.stages, 0),
  });
  let instability = addInstability(state.instability, gain);
  let crises = state.crises;
  let projects = state.projects;
  let nextId = state.nextId;
  const rng = new Rng(state.rng);
  const plan = (
    kind: CrisisKindId,
    cause: Crisis['cause'],
    phase: number | null = null,
    announceAt = tick + CRISIS_DIRECTOR.foresightTicks,
    strikeAt = announceAt + CRISIS_DIRECTOR.warningTicks,
  ) => {
    const crisis: Crisis = {
      id: nextId++,
      kind,
      sector: CRISIS_KINDS[kind].sectored ? rng.int(0, 5) : null,
      cause,
      plannedAt: tick,
      announceAt,
      strikeAt,
      warned: [],
      phase,
    };
    crises = [...crises, crisis];
    // Its joint project opens at once: whoever hears first can start giving.
    const projectKind = CRISIS_KINDS[kind].project;
    const project: Project = {
      id: nextId++,
      crisisId: crisis.id,
      kind: projectKind,
      hex: projectHex(state.system, crisis.sector, PROJECTS[projectKind].distance),
      deadline: crisis.strikeAt,
      ...projectSize(projectKind, state.players.length),
      byPlayer: {},
    };
    projects = [...projects, project];
    events.push({
      type: 'projectOpened',
      projectId: project.id,
      crisisId: crisis.id,
      kind: projectKind,
      hex: project.hex,
      threshold: project.threshold,
      full: project.full,
      deadline: project.deadline,
    });
  };

  const finalDue =
    instability.value >= INSTABILITY.max ||
    tick >= CRISIS_DIRECTOR.finalWarningTick - CRISIS_DIRECTOR.foresightTicks;
  if (!instability.finalPlanned && finalDue) {
    // The pulsation in phases: each is announced when the one before strikes.
    const cause = chooseCrisis(instability.sinceCrisis, state.system.starClass, rng).cause;
    const first = tick + CRISIS_DIRECTOR.foresightTicks + CRISIS_DIRECTOR.warningTicks;
    for (let phase = 1; phase <= CRISIS_DIRECTOR.finalPhases; phase++) {
      const strikeAt = first + (phase - 1) * CRISIS_DIRECTOR.finalPhaseTicks;
      const announceAt =
        phase === 1
          ? tick + CRISIS_DIRECTOR.foresightTicks
          : strikeAt - CRISIS_DIRECTOR.finalPhaseTicks;
      plan('stellarPulsation', cause, phase, announceAt, strikeAt);
    }
    instability = { ...instability, finalPlanned: true };
  }

  const reached = thresholdsReached(instability.value);
  if (reached > instability.thresholdsPassed) {
    const threshold = INSTABILITY.thresholds[instability.thresholdsPassed]!;
    const busy = crises.some((c) => !CRISIS_KINDS[c.kind].final);
    const tooLate = instability.finalPlanned || tick > CRISIS_DIRECTOR.lastRegularPlanTick;
    if (tooLate) {
      // The final crisis takes over: thresholds passed now set off nothing more.
      for (let i = instability.thresholdsPassed; i < reached; i++) {
        events.push({
          type: 'instabilityThreshold',
          threshold: INSTABILITY.thresholds[i]!,
          value: instability.value,
        });
      }
      instability = { ...instability, thresholdsPassed: reached };
    } else if (!busy) {
      events.push({ type: 'instabilityThreshold', threshold, value: instability.value });
      const { kind, cause } = chooseCrisis(instability.sinceCrisis, state.system.starClass, rng);
      plan(kind, cause);
      instability = {
        ...instability,
        thresholdsPassed: instability.thresholdsPassed + 1,
        sinceCrisis: createInstability().sinceCrisis,
      };
    }
  }
  return { ...state, instability, crises, projects, nextId, rng: rng.getState() };
}

/**
 * Crises on their way, every tick: each player hears of a crisis when it is
 * announced, or earlier with foresight (#27); at its hour it strikes (#29).
 */
function runCrises(state: GameState, tick: number, events: GameEvent[]): GameState {
  const crises: Crisis[] = [];
  let next = state;
  for (const c of state.crises) {
    const warned = [...c.warned];
    for (const p of state.players) {
      if (warned.includes(p.id)) continue;
      const lead = techMods(p.techs).crisisWarningTicks;
      if (tick < c.announceAt - lead) continue;
      warned.push(p.id);
      events.push({
        type: 'crisisWarning',
        playerId: p.id,
        crisisId: c.id,
        kind: c.kind,
        sector: c.sector,
        strikeAt: c.strikeAt,
        project: CRISIS_KINDS[c.kind].project,
        early: tick < c.announceAt,
        phase: c.phase,
      });
    }
    if (tick >= c.strikeAt) {
      events.push({ type: 'crisisStruck', crisisId: c.id, kind: c.kind, sector: c.sector });
      next = resolveCrisis(next, c, tick, events);
      continue;
    }
    crises.push(warned.length === c.warned.length ? c : { ...c, warned });
  }
  return { ...next, crises };
}

function updatePlayer(
  state: GameState,
  id: string,
  update: (p: PlayerState) => PlayerState,
): GameState {
  return { ...state, players: state.players.map((p) => (p.id === id ? update(p) : p)) };
}

/**
 * A crisis strikes (GDD §9). Its project settles first: built in time, it
 * spares contributors (or everyone, at full cover), pays Prestige by share
 * and eases the index; short of the threshold, every contribution goes back.
 * Then the blow lands on everyone not spared, in its sector or everywhere.
 */
function resolveCrisis(state: GameState, c: Crisis, tick: number, events: GameEvent[]): GameState {
  let next = state;
  let outcome: ProjectOutcome = 'failed';
  let covered: string[] = [];
  const project = state.projects.find((p) => p.crisisId === c.id);
  if (project) {
    outcome = projectOutcome(project);
    const prestige: Record<string, number> = {};
    if (outcome === 'failed') {
      next = refundProject(next, project);
    } else {
      const total = projectTotal(project);
      for (const p of next.players) {
        const given = project.byPlayer[p.id]?.points ?? 0;
        if (given <= 0) continue;
        const bonus = 1 + techMods(p.techs).projectPrestige;
        prestige[p.id] = roundMoney(((PROJECTS[project.kind].prestige * given) / total) * bonus);
      }
      next = {
        ...next,
        players: next.players.map((p) =>
          prestige[p.id] ? { ...p, prestige: roundMoney(p.prestige + prestige[p.id]!) } : p,
        ),
        instability: {
          ...next.instability,
          value: Math.max(0, next.instability.value - PROJECT_RULES.instabilityRelief),
        },
      };
    }
    covered = coveredPlayers(
      project,
      next.players.map((p) => p.id),
    );
    events.push({
      type: 'projectResolved',
      projectId: project.id,
      crisisId: c.id,
      outcome,
      prestige,
    });
    next = { ...next, projects: next.projects.filter((p) => p.id !== project.id) };
  }
  if (CRISIS_KINDS[c.kind].final) {
    // Every stage must stand: one phase let through and the system is lost.
    const finalStages = [...next.finalStages, outcome];
    const last = (c.phase ?? CRISIS_DIRECTOR.finalPhases) >= CRISIS_DIRECTOR.finalPhases;
    const finalCrisis = !last ? null : finalStages.includes('failed') ? 'failed' : 'averted';
    next = { ...next, finalStages, finalCrisis };
  }
  return strikeCrisis(next, c, covered, tick, events);
}

/** A failed project hands everything back: goods, science, and ships at the capital. */
function refundProject(state: GameState, project: Project): GameState {
  let nextId = state.nextId;
  const fleets: FleetState[] = [];
  const players = state.players.map((p) => {
    const c = project.byPlayer[p.id];
    if (!c) return p;
    const goods = { ...p.goods };
    for (const [g, n] of Object.entries(c.goods) as [TradeGoodId, number][]) goods[g] += n;
    if (c.ships.length > 0) {
      const ships = c.ships.map((cls) => ({ id: nextId++, cls }));
      fleets.push({
        id: nextId++,
        owner: p.id,
        hex: capitalHex(state, p.id),
        ships,
        path: [],
        progress: 0,
        scan: null,
      });
    }
    return { ...p, goods, planet: { ...p.planet, science: p.planet.science + c.science } };
  });
  return { ...state, players, nextId, fleets: [...state.fleets, ...fleets] };
}

/** The blow itself, on everyone not covered: capitals, outposts and ships in its reach. */
function strikeCrisis(
  state: GameState,
  c: Crisis,
  covered: readonly string[],
  tick: number,
  events: GameEvent[],
): GameState {
  const fx = CRISIS_EFFECTS[c.kind];
  const inReach = (h: Hex) => c.sector === null || sectorOf(h) === c.sector;
  const exposed = (owner: string) => !covered.includes(owner);
  const screens = (owner: string) => {
    const p = state.players.find((x) => x.id === owner);
    return p ? techMods(p.techs).crisisDamage : 1;
  };
  const report = new Map<
    string,
    { pop: number; blackout: number; outposts: number; ships: number }
  >();
  const note = (id: string) => {
    let r = report.get(id);
    if (!r) report.set(id, (r = { pop: 0, blackout: 0, outposts: 0, ships: 0 }));
    return r;
  };

  const players = state.players.map((p) => {
    if (!exposed(p.id) || !inReach(capitalHex(state, p.id))) return p;
    const shield = p.planet.buildings.some((b) => b.id === 'planetaryShield' && b.level > 0);
    const m = screens(p.id) * (shield ? SHIELD_CRISIS_FACTOR : 1);
    const population = Math.max(
      ECONOMY.minPopulation,
      p.planet.population * (1 - fx.populationLoss * m),
    );
    const goods = Object.fromEntries(
      TRADE_GOOD_IDS.map((g) => [g, p.goods[g] * (1 - fx.goodsLoss * m)]),
    ) as Record<TradeGoodId, number>;
    const cycles = Math.ceil(fx.blackoutCycles * m);
    const r = note(p.id);
    r.pop = p.planet.population - population;
    r.blackout = cycles;
    return {
      ...p,
      goods,
      planet: { ...p.planet, population },
      blackout:
        cycles > 0
          ? { until: tick + cycles * BALANCE.cycleTicks, energy: 1 - (1 - fx.blackoutEnergy) * m }
          : p.blackout,
    };
  });

  const outposts = state.outposts.map((o) => {
    if (!exposed(o.owner) || !inReach(o.hex)) return o;
    const cycles = Math.ceil(fx.outpostDownCycles * screens(o.owner));
    if (cycles <= 0) return o;
    note(o.owner).outposts++;
    return { ...o, downUntil: Math.max(o.downUntil ?? 0, tick + cycles * BALANCE.cycleTicks) };
  });

  const fleets: FleetState[] = [];
  for (const f of state.fleets) {
    if (!exposed(f.owner) || !inReach(f.hex) || fx.hullLoss <= 0) {
      fleets.push(f);
      continue;
    }
    const m = f.owner === PIRATES ? 1 : screens(f.owner);
    const ships = [];
    for (const s of f.ships) {
      const hull = shipHull(s) - SHIP_COMBAT[s.cls].hull * fx.hullLoss * m;
      if (hull > 0) {
        ships.push({ ...s, hull });
        continue;
      }
      if (f.owner !== PIRATES) note(f.owner).ships++;
      events.push({ type: 'shipDestroyed', playerId: f.owner, ship: s.cls, hex: f.hex });
    }
    if (ships.length > 0) fleets.push({ ...f, ships });
    else
      events.push({ type: 'fleetDestroyed', playerId: f.owner, fleetId: f.id, lead: leadShip(f) });
  }

  for (const [playerId, r] of report) {
    events.push({
      type: 'crisisDamage',
      playerId,
      kind: c.kind,
      populationLost: Math.round(r.pop * 100) / 100,
      blackoutCycles: r.blackout,
      outpostsDown: r.outposts,
      shipsLost: r.ships,
    });
  }
  // Pirate bands the blow wiped out are gone, with no loot for anyone.
  const left = new Set(fleets.map((f) => f.id));
  const pirates = state.pirates.filter((b) => left.has(b.fleetId));
  // Whoever the crisis hit has failed it: their vassals may rise (GDD §8).
  const hit = players.map((p) => (report.has(p.id) ? { ...p, failedCrisisAt: tick } : p));
  return { ...state, players: hit, outposts, fleets, pirates };
}

/** Prestige every cycle (GDD §10): a share of the system's output and the Lagrange stations held. */
function runPrestige(state: GameState): GameState {
  const outputs = state.players.map((p) => p.currency.outputValue.at(-1) ?? 0);
  const total = outputs.reduce((a, b) => a + b, 0);
  return {
    ...state,
    players: state.players.map((p, i) => {
      const lagrange = state.outposts.filter(
        (o) => o.owner === p.id && o.kind === 'lagrangeStation',
      ).length;
      const gain =
        (total > 0 ? (PRESTIGE.economyPerCycle * outputs[i]!) / total : 0) +
        PRESTIGE.lagrangePerCycle * lagrange;
      return gain > 0 ? { ...p, prestige: roundMoney(p.prestige + gain) } : p;
    }),
  };
}

/**
 * Dyson swarms each tick: a stage finishes, the next starts once its
 * resources are there; a hostile fleet holding the site with no defender
 * for long enough rolls a stage back (GDD §10).
 */
function runMegastructures(state: GameState, tick: number, events: GameEvent[]): GameState {
  let players = state.players;
  const megastructures = state.megastructures.map((m) => {
    let next = m;
    if (next.buildingUntil !== null && tick >= next.buildingUntil) {
      next = { ...next, stages: next.stages + 1, buildingUntil: null };
      events.push({ type: 'megastructureStage', playerId: m.owner, stages: next.stages });
    }
    const owner = players.find((p) => p.id === m.owner)!;
    const cost = MEGASTRUCTURE.stageCost;
    if (
      next.buildingUntil === null &&
      next.stages < MEGASTRUCTURE.stages &&
      owner.goods.alloys >= cost.alloys &&
      owner.goods.exotics >= cost.exotics
    ) {
      players = players.map((p) =>
        p.id === m.owner
          ? {
              ...p,
              goods: {
                ...p.goods,
                alloys: p.goods.alloys - cost.alloys,
                exotics: p.goods.exotics - cost.exotics,
              },
            }
          : p,
      );
      next = { ...next, buildingUntil: tick + MEGASTRUCTURE.stageTicks };
    }
    const here = state.fleets.filter((f) => hexKey(f.hex) === hexKey(m.hex) && isArmed(f));
    const attacker = here.find((f) => f.owner !== PIRATES && atWar(state, f.owner, m.owner));
    const defended = here.some((f) => f.owner === m.owner);
    if (!attacker || defended) return next.hold ? { ...next, hold: null } : next;
    const hold = next.hold?.by === attacker.owner ? next.hold : { by: attacker.owner, since: tick };
    if (tick - hold.since < MEGASTRUCTURE.holdTicks) return { ...next, hold };
    const stages = next.buildingUntil !== null ? next.stages : Math.max(0, next.stages - 1);
    events.push({ type: 'megastructureSetBack', playerId: m.owner, by: attacker.owner, stages });
    return { ...next, stages, buildingUntil: null, hold: null };
  });
  return { ...state, players, megastructures };
}

/** Whether a countdown victory's condition holds for `id` right now (GDD §10). */
function victoryHolds(state: GameState, id: string, kind: CountdownVictoryId): boolean {
  if (kind === 'domination') {
    // With teams (#137) the team holds the realm together: its vassals and stations.
    const team = new Set(teamOf(state.players, id));
    const rivals = state.players.filter((p) => !team.has(p.id));
    const ours = (p: PlayerState) => p.vassalOf !== null && team.has(p.vassalOf);
    if (rivals.length > 0 && rivals.every(ours)) return true;
    if (state.players.length < 3) return false;
    // Most of the Lagrange stations standing (GDD §10), a vassal's counting as the
    // overlord's, and a vassal.
    const realm = new Set([...team, ...rivals.filter(ours).map((p) => p.id)]);
    const stations = state.outposts.filter((o) => o.kind === 'lagrangeStation');
    const held = stations.filter((o) => realm.has(o.owner)).length;
    return (
      held > 0 && held > stations.length * VICTORY.dominationLagrangeShare && rivals.some(ours)
    );
  }
  if (kind === 'economic') {
    // A sanctioned currency is no anchor for the system: no countdown starts under
    // sanctions, and a running one pauses (runVictory).
    if (state.reserve.issuer !== id || isSanctioned(state.levers, id)) return false;
    return economicShareHolds(state, id);
  }
  return state.megastructures.some((m) => m.owner === id && m.stages >= MEGASTRUCTURE.stages);
}

/** The issuer's share of the system's output is above the economic victory's bar. */
function economicShareHolds(state: GameState, id: string): boolean {
  if (state.reserve.issuer !== id) return false;
  const outputs = state.players.map((p) => windowOutput(p.currency));
  const total = outputs.reduce((a, b) => a + b, 0);
  const mine = windowOutput(state.players.find((p) => p.id === id)!.currency);
  const need = VICTORY.economicShare[state.players.length] ?? 0.5;
  return total > 0 && mine / total >= need - 1e-9;
}

const COUNTDOWN_KINDS: readonly CountdownVictoryId[] = ['domination', 'economic', 'technological'];

/**
 * The Prestige leader, who wins a joint or time victory. With teams (#137) the team
 * with the highest average Prestige wins (the author's call of 27.10.2026: fair to
 * teams of different sizes), named by its own leader.
 */
const leader = (state: GameState): string | null => {
  const table = standings(state.players);
  if (!state.players.some((p) => p.team !== undefined)) return table[0]?.playerId ?? null;
  const total = (id: string) => {
    const team = teamOf(state.players, id);
    return (
      team.reduce((sum, m) => sum + (state.players.find((p) => p.id === m)?.prestige ?? 0), 0) /
      team.length
    );
  };
  const best = [...table].sort((a, b) => total(b.playerId) - total(a.playerId))[0];
  return best?.playerId ?? null;
};

function endMatch(
  state: GameState,
  kind: MatchResult['kind'],
  winner: string | null,
  events: GameEvent[],
): GameState {
  const result: MatchResult = {
    kind,
    winner,
    winners: winner === null ? [] : teamOf(state.players, winner),
    tick: state.tick,
    standings: standings(state.players),
  };
  events.push({ type: 'matchEnded', result });
  return { ...state, status: 'ended', result, countdowns: [] };
}

/**
 * Victories each tick (GDD §10). The final crisis ends the match (joint
 * victory or collapse), unless the lobby plays on; countdowns start when a
 * condition holds at a cycle's close, stop the moment it breaks, and win
 * when they run out.
 */
function runVictory(before: GameState, tick: number, events: GameEvent[]): GameState {
  let state = before;
  if (state.finalCrisis === 'failed') return endMatch(state, 'collapse', null, events);
  if (state.finalCrisis === 'averted') {
    if (state.mode === 'crisisFinale') return endMatch(state, 'joint', leader(state), events);
    if (state.endsAt === null) state = { ...state, endsAt: tick + VICTORY.lifeAfterChaosTicks };
    else if (tick >= state.endsAt) return endMatch(state, 'joint', leader(state), events);
  }
  const cycle = tick % BALANCE.cycleTicks === 0;
  if (!cycle && state.countdowns.length === 0) return state;

  let countdowns: Countdown[] = [];
  for (let c of state.countdowns) {
    const vassal = state.players.find((p) => p.id === c.playerId)?.vassalOf !== null;
    // Sanctions on the issuer pause the economic countdown (#133, the author's call of
    // 26.10.2026): it stands while they last, and stops only if the share falls short.
    const paused =
      !vassal &&
      c.kind === 'economic' &&
      isSanctioned(state.levers, c.playerId) &&
      economicShareHolds(state, c.playerId);
    if (paused) {
      if (!c.paused) {
        events.push({
          type: 'countdownPaused',
          playerId: c.playerId,
          kind: c.kind,
          endsAt: c.endsAt,
        });
      }
      countdowns.push({ ...c, endsAt: c.endsAt + 1, paused: true });
      continue;
    }
    const holds = !vassal && victoryHolds(state, c.playerId, c.kind);
    if (holds && c.paused) {
      events.push({
        type: 'countdownResumed',
        playerId: c.playerId,
        kind: c.kind,
        endsAt: c.endsAt,
      });
      c = { playerId: c.playerId, kind: c.kind, endsAt: c.endsAt };
    }
    if (!holds) {
      events.push({
        type: 'countdownStopped',
        playerId: c.playerId,
        kind: c.kind,
        endsAt: c.endsAt,
      });
      continue;
    }
    if (tick >= c.endsAt) return endMatch(state, c.kind, c.playerId, events);
    countdowns.push(c);
  }
  if (cycle) {
    for (const p of state.players) {
      if (p.vassalOf !== null) continue;
      for (const kind of COUNTDOWN_KINDS) {
        if (countdowns.some((c) => c.playerId === p.id && c.kind === kind)) continue;
        if (!victoryHolds(state, p.id, kind)) continue;
        const c: Countdown = { playerId: p.id, kind, endsAt: tick + VICTORY.countdownTicks };
        countdowns = [...countdowns, c];
        events.push({ type: 'victoryCountdown', playerId: p.id, kind, endsAt: c.endsAt });
      }
    }
  }
  return { ...state, countdowns };
}

/** What an exchange deal would cost or fetch a player right now (GDD §6), or why it cannot go. */
export type TradeTerms =
  | {
      readonly ok: true;
      readonly quote: Quote;
      /** In the player's currency: paid when buying, received when selling. */
      readonly local: number;
      readonly conversion: number;
      readonly surcharge: number;
    }
  | { readonly ok: false; readonly reason: string };

/**
 * The terms of an exchange deal, exactly as `step` settles it: the quote in
 * ГС, converted at the player's rate, less the conversion fee and any
 * sanctions surcharge. The UI shows these numbers before the player clicks.
 */
export function tradeTerms(
  state: GameState,
  playerId: string,
  good: TradeGoodId,
  side: TradeSide,
  quantity: number,
): TradeTerms {
  const player = state.players.find((p) => p.id === playerId);
  if (!player) return { ok: false, reason: 'no such player' };
  // Embargo (GDD §6): the target cannot buy the embargoer's goods — its faction's cheap good.
  if (
    side === 'buy' &&
    state.players.some(
      (p) => FACTIONS[p.faction].cheapGood === good && hasEmbargo(state.levers, p.id, player.id),
    )
  ) {
    return { ok: false, reason: 'embargo' };
  }
  const q = quote(state.market, good, side, quantity);
  // The exchange settles in ГС; the player pays or receives their own
  // currency at the current rate, less the conversion fee. The exchange
  // runs on the reserve currency, so its issuer converts nothing.
  const atRate = q.total / player.currency.rate;
  const fee = techMods(player.techs).conversionFee;
  const conversion = state.reserve.issuer === player.id ? 0 : roundMoney(atRate * fee);
  // Sanctions (GDD §6): the target pays extra on every deal, both ways.
  const surcharge = isSanctioned(state.levers, player.id)
    ? roundMoney(atRate * LEVER_EFFECTS.sanctionsSurcharge)
    : 0;
  const local = roundMoney(
    side === 'buy' ? atRate + conversion + surcharge : atRate - conversion - surcharge,
  );
  if (side === 'buy' && local > player.cash) return { ok: false, reason: 'not enough cash' };
  if (side === 'sell' && player.goods[good] < quantity) {
    return { ok: false, reason: 'not enough goods' };
  }
  return { ok: true, quote: q, local, conversion, surcharge };
}

/**
 * Why `step` would refuse `command` right now, or null if it would take it.
 * Runs the command alone, without advancing time, so the UI can grey out a
 * button with the simulation's own reason instead of guessing at the rules.
 */
export function commandBlocker(state: GameState, command: Command): string | null {
  const events: GameEvent[] = [];
  applyCommand(state, command, events);
  const rejected = events.find((e) => e.type === 'commandRejected');
  return rejected?.type === 'commandRejected' ? rejected.reason : null;
}

/** Advance the match by one tick. Pure: never mutates `state`. */
/**
 * Buildings finish the tick their construction is due (#150: they waited at 100% for
 * the next cycle, up to 30 s). They work from the next cycle on, as before.
 */
function runConstructions(state: GameState, tick: number, events: GameEvent[]): GameState {
  const due = (p: PlayerState) =>
    p.planet.buildings.some((b) => b.construction && b.construction.readyTick <= tick);
  if (!state.players.some(due)) return state;
  return {
    ...state,
    players: state.players.map((p) => {
      if (!due(p)) return p;
      const buildings = p.planet.buildings.map((b, index) => {
        if (!b.construction || b.construction.readyTick > tick) return b;
        const level = b.construction.targetLevel;
        events.push({
          type: 'constructionCompleted',
          playerId: p.id,
          index,
          building: b.id,
          level,
        });
        return { ...b, level, construction: null };
      });
      return { ...p, planet: { ...p.planet, buildings } };
    }),
  };
}

export function step(state: GameState, commands: readonly Command[] = []): StepResult {
  if (state.status === 'ended') return { state, events: [] };
  const events: GameEvent[] = [];
  let next = state;
  for (const command of commands) next = applyCommand(next, command, events);

  if (next.pause) {
    const ticksLeft = next.pause.ticksLeft - 1;
    if (ticksLeft <= 0) {
      events.push({ type: 'resumed', playerId: null });
      next = { ...next, pause: null };
    } else {
      next = { ...next, pause: { ...next.pause, ticksLeft } };
    }
    return { state: next, events };
  }

  const tick = next.tick + 1;
  next = runPirates(
    runFindOffers(runScans(runFleets(runShipyards(next, events), events), events), events),
    events,
  );
  next = runCaravans(runDiplomacy(next, events), events);
  // Combat needs a war, or pirates around; otherwise only a battle still winding down.
  const anyFoes = next.wars.length > 0 || next.fleets.some((f) => f.owner === PIRATES);
  if (next.tick % COMBAT.roundTicks === 0 && (anyFoes || next.battles.length > 0)) {
    next = settlePirates(next, runCombat(next, events), events);
  }
  if (next.levers.some((l) => l.until <= tick)) next = endLevers(next, tick, events);
  if (next.crises.length > 0) next = runCrises(next, tick, events);
  next = runConstructions(next, tick, events);
  let market = revertPrices(next.market, tick);
  if (tick % BALANCE.cycleTicks === 0) {
    const closed = runMarketEvents({ ...next, market: resetCycleVolume(market) }, tick, events);
    market = closed.market;
    next = runCycle(closed, tick, market, events);
    next = runAutoTrade({ ...next, market }, events);
    market = next.market;
    next = runTribute(runFleetCycle(runResearch(next, events)), events);
    next = runDirector(runPrestige(next), tick, events);
  }
  if (next.megastructures.length > 0) next = runMegastructures(next, tick, events);
  next = explore(runVictory({ ...next, tick, market }, tick, events));
  if (next.status === 'running' && tick >= BALANCE.sessionLimitTicks) {
    events.push({ type: 'sessionLimitReached' });
    next = endMatch(next, 'time', leader(next), events);
  }
  return { state: next, events };
}
